import React, { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  initialGameState,
  startGame,
  discardCard,
  pickUpFromPile,
  pickUpAndDiscard,
  callSerg,
  finalizeSerg,
  setSurgeReady,
  nextRound,
  reorderHand,
  createPile,
  addCardsToPile,
  breakPile,
  returnCardFromPile,
  surgeGraceEqualizeDiscard,
  PHASES,
} from '../game/gameState';
import { getBotMove, getBotDelay } from '../game/bot';
import { createConnection, generateRoomCode } from '../game/multiplayer';
import { createSoundManager } from '../game/sound';
import { JAMES_BOND_QUOTES } from '../data/jamesBondQuotes';
import PokerTable from '../components/PokerTable';
import HandFan from '../components/HandFan';
import FlyingCard from '../components/FlyingCard';
import Martini from '../components/Martini';
import Grainient from '../components/Grainient';
import surgeName from '../Images/SURGE_NAME.svg';
import './Game.css';

const BOT_DELAY_FALLBACK_MS = 900;
/** Chance each bot turn shows a random Bond quote above their seat (online martini quotes are separate). */
const BOT_BOND_QUOTE_CHANCE = 0.09;
const PICKUP_DRAG_THRESHOLD_PX = 8;
const STORAGE_KEY = 'surge-game-data';
const CANNOT_DISCARD_PICKED_UP_MSG = 'You cannot put down the card you just picked up.';
const SURGE_EQUALIZE_HINT =
  'You have 11 cards — put one down. It is given to the player with 9 (you cannot put down the card you just picked up).';
const CHAT_PANEL_MIN_WIDTH = 320;
const CHAT_PANEL_MIN_HEIGHT = 280;
const CHAT_PANEL_MAX_WIDTH = 560;
const CHAT_PANEL_MAX_HEIGHT = 680;
const CHAT_PANEL_MARGIN = 8;
const CHAT_MAX_MESSAGES = 100;
const SPECIAL_HAND_LABELS = {
  straight_flush: 'Straight Flush',
  full_house: 'Full House',
  quad: 'Four of a Kind',
  flush: 'Flush',
  straight: 'Straight',
  triple: 'Triple',
};
const PLAYER_QUOTE_BUBBLE_MS = 5200;
const BOT_CHAT_REPLIES = [
  'Nice play.',
  'Interesting move.',
  'Good timing.',
  'I did not see that coming.',
  'Let me think...',
  'This round is close.',
  'Well played.',
];

const POINTS_SUIT = { s: '♠', h: '♥', d: '♦', c: '♣' };
const pointsRank = (r) => (r === 1 ? 'A' : r === 11 ? 'J' : r === 12 ? 'Q' : r === 13 ? 'K' : String(r));
const POINTS_HAND_EXAMPLES = [
  [[7, 's'], [7, 'h']],
  [[10, 'c'], [10, 'd'], [10, 'h']],
  [[4, 's'], [4, 'h'], [4, 'd'], [4, 'c']],
  [[13, 's'], [13, 'h'], [13, 'd'], [2, 'c'], [2, 'h']],
  [[5, 's'], [6, 'h'], [7, 'd'], [8, 'c'], [9, 's']],
  [[2, 'h'], [5, 'h'], [8, 'h'], [11, 'h'], [1, 'h']],
  [[3, 's'], [4, 's'], [5, 's'], [6, 's'], [7, 's']],
];

function PointsCardFan({ cards }) {
  return (
    <div className="game-screen__points-fan" aria-hidden="true">
      {cards.map(([rank, suit], i) => {
        const isRed = suit === 'h' || suit === 'd';
        return (
          <div
            key={`${rank}-${suit}-${i}`}
            className={`game-screen__points-card-top ${isRed ? 'game-screen__points-card-top--red' : ''}`}
          >
            <span className="game-screen__points-card-rank">{pointsRank(rank)}</span>
            <span className="game-screen__points-card-suit">{POINTS_SUIT[suit]}</span>
          </div>
        );
      })}
    </div>
  );
}

const DEFAULT_SETTINGS = {
  botDifficulty: 'medium',
  soundMuted: false,
  ambienceEnabled: false,
  theme: 'classic',
};

const DEFAULT_STATS = {
  gamesPlayed: 0,
  wins: 0,
  highScore: 0,
  lastRecordedGameId: null,
};

const THEMES = {
  classic: {
    label: 'Classic',
    table: '#2e7d32',
    cardBackStart: '#1a472a',
    cardBackEnd: '#2d5a3d',
  },
  midnight: {
    label: 'Midnight',
    table: '#1f3b64',
    cardBackStart: '#0f1a2b',
    cardBackEnd: '#243b5a',
  },
};

const createClientGameId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizePlayerNames = (count, names) => {
  const fallback = Array.from({ length: count }, (_, i) => `Player ${i + 1}`);
  if (!Array.isArray(names)) return fallback;
  return fallback.map((name, idx) => {
    const candidate = String(names[idx] ?? '').trim();
    return candidate ? candidate : name;
  });
};

const normalizeSettings = (settings) => ({ ...DEFAULT_SETTINGS, ...(settings || {}) });
const normalizeStats = (stats) => ({ ...DEFAULT_STATS, ...(stats || {}) });
const pickBondQuote = (exclude = null) => {
  if (!Array.isArray(JAMES_BOND_QUOTES) || JAMES_BOND_QUOTES.length === 0) return 'Shaken, not stirred.';
  if (JAMES_BOND_QUOTES.length === 1) return JAMES_BOND_QUOTES[0];
  let chosen = JAMES_BOND_QUOTES[0];
  for (let guard = 0; guard < 12; guard += 1) {
    const candidate = JAMES_BOND_QUOTES[Math.floor(Math.random() * JAMES_BOND_QUOTES.length)];
    if (candidate !== exclude) {
      chosen = candidate;
      break;
    }
  }
  return chosen;
};

const QUICK_START_STEPS = [
  { keyword: 'Aim', body: 'Reach 40 points to win.' },
  { keyword: 'Deal', body: 'Each player gets 10 cards.' },
  {
    keyword: 'Take turns',
    body: 'Drop a card in the centre; the next player picks it up and swaps it for one of theirs.',
  },
  { keyword: 'Build piles', body: 'Arrange your hand into scoring combinations.' },
  { keyword: 'Call SURGE', body: 'Ends the round and starts a 20 second grace period.' },
  {
    keyword: 'Score',
    body: 'Valid pile hands score points. Unused cards are -1. All cards used is a +1 bonus.',
  },
  { keyword: 'Repeat', body: 'Totals carry over and the next round starts.' },
];

const loadStoredData = () => {
  if (typeof window === 'undefined') {
    return { state: null, playerCount: 2, playerNames: null, settings: DEFAULT_SETTINGS, stats: DEFAULT_STATS };
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { state: null, playerCount: 2, playerNames: null, settings: DEFAULT_SETTINGS, stats: DEFAULT_STATS };
    }
    const parsed = JSON.parse(raw);
    return {
      state: parsed?.state ?? null,
      playerCount: Number.isFinite(parsed?.playerCount) ? parsed.playerCount : 2,
      playerNames: Array.isArray(parsed?.playerNames) ? parsed.playerNames : null,
      settings: normalizeSettings(parsed?.settings),
      stats: normalizeStats(parsed?.stats),
    };
  } catch (err) {
    return { state: null, playerCount: 2, playerNames: null, settings: DEFAULT_SETTINGS, stats: DEFAULT_STATS };
  }
};

const saveStoredData = (payload) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (err) {
    // Ignore write errors (private mode, quota, etc.)
  }
};

const clearStoredActiveGame = (overrides = {}) => {
  const stored = loadStoredData();
  saveStoredData({
    state: null,
    playerCount: overrides.playerCount ?? stored.playerCount ?? 2,
    playerNames: overrides.playerNames ?? stored.playerNames ?? null,
    settings: overrides.settings ?? stored.settings ?? DEFAULT_SETTINGS,
    stats: overrides.stats ?? stored.stats ?? DEFAULT_STATS,
  });
};

const normalizeStoredState = (storedState, fallbackCount, fallbackNames, settings) => {
  if (!storedState || !storedState.phase || !Array.isArray(storedState.hands)) return null;
  const numPlayers = Number.isFinite(storedState.numPlayers) ? storedState.numPlayers : fallbackCount;
  const names = normalizePlayerNames(numPlayers, storedState.playerNames || fallbackNames);
  return {
    ...storedState,
    numPlayers,
    playerNames: names,
    botDifficulty: storedState.botDifficulty || settings.botDifficulty,
    roundNumber: storedState.roundNumber ?? 1,
    gameId: storedState.gameId || createClientGameId(),
  };
};

export default function Game() {
  const storedDataRef = useRef(loadStoredData());
  const storedData = storedDataRef.current;
  const skipDealAnimationRef = useRef(
    normalizeStoredState(
      storedData.state,
      storedData.playerCount ?? storedData.state?.numPlayers ?? 2,
      normalizePlayerNames(
        storedData.playerCount ?? storedData.state?.numPlayers ?? 2,
        storedData.playerNames
      ),
      storedData.settings
    )?.phase === PHASES.PLAYING
  );
  /** Rounds that already finished the intro deal animation (or skipped on restore). */
  const finishedDealKeysRef = useRef(new Set());
  const activeDealKeyRef = useRef(null);
  const initialPlayerCount = storedData.playerCount ?? storedData.state?.numPlayers ?? 2;
  const initialPlayerNames = normalizePlayerNames(
    initialPlayerCount,
    storedData.playerNames || storedData.state?.playerNames
  );

  const [settings, setSettings] = useState(storedData.settings);
  const [stats, setStats] = useState(storedData.stats);
  const [playerCount, setPlayerCount] = useState(initialPlayerCount);
  const [playerNames, setPlayerNames] = useState(initialPlayerNames);
  const [state, setState] = useState(() => {
    const normalizedState = normalizeStoredState(
      storedData.state,
      initialPlayerCount,
      initialPlayerNames,
      storedData.settings
    );
    return (
      normalizedState ||
      initialGameState(initialPlayerCount, {
        playerNames: initialPlayerNames,
        botDifficulty: storedData.settings.botDifficulty,
      })
    );
  });
  /** Selected card ids in hand (player 0): 1 → Put card down; 2+ → Make pile */
  const [handSelectedIds, setHandSelectedIds] = useState([]);
  const [dragOverUserPileIndex, setDragOverUserPileIndex] = useState(null);
  const [flyingCard, setFlyingCard] = useState(null);
  /** Round start: deck in center, one card at a time round-robin to each seat. */
  const [dealAnimation, setDealAnimation] = useState(null);
  /** Face-down cards placed so far per player (shown under flying card). */
  const [dealDealtCounts, setDealDealtCounts] = useState(null);
  const dealFlightStepRef = useRef(0);
  const dealAnimatingRef = useRef(false);
  dealAnimatingRef.current = Boolean(dealAnimation);
  if (dealAnimation?.flight) dealFlightStepRef.current = dealAnimation.step;
  /** SURGE scoring sequence before committing round (local / bots only). */
  const [surgeReveal, setSurgeReveal] = useState(null);
  const [surgeFlyScore, setSurgeFlyScore] = useState(null);
  const [surgeOtherFlyScore, setSurgeOtherFlyScore] = useState(null);
  const [surgeCallSplash, setSurgeCallSplash] = useState(null);
  const [surgeGraceNow, setSurgeGraceNow] = useState(() => Date.now());
  const animatedRoundKeyRef = useRef(null);
  const [gameWinnerRevealStep, setGameWinnerRevealStep] = useState(0);
  const [tooltip, setTooltip] = useState(null);
  const [tooltipAnchor, setTooltipAnchor] = useState('fan');
  const tooltipTimerRef = useRef(null);
  const tooltipHintsShownRef = useRef({ discard: false, pile: false, pickupRule: false, privatePile: false });
  const tooltipHintsGameIdRef = useRef(null);
  const [showRulesOverlay, setShowRulesOverlay] = useState(false);
  const [showPointsOverlay, setShowPointsOverlay] = useState(false);
  const [showQuickStartOverlay, setShowQuickStartOverlay] = useState(false);
  const [showLobbyConfirm, setShowLobbyConfirm] = useState(false);

  // Multiplayer
  const [lobbyStep, setLobbyStep] = useState('choose'); // 'choose' | 'setup'
  const [gameMode, setGameMode] = useState('bots'); // 'bots' | 'online'
  const [onlineLobby, setOnlineLobby] = useState(null);
  const [onlineState, setOnlineState] = useState(null); // eslint-disable-line no-unused-vars
  const [onlinePlayerIndex, setOnlinePlayerIndex] = useState(0); // eslint-disable-line no-unused-vars
  const [roomCode, setRoomCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [onlineError, setOnlineError] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [chatDraft, setChatDraft] = useState('');
  const [chatPosition, setChatPosition] = useState(null);
  const [chatSize, setChatSize] = useState(null);
  const [chatDragging, setChatDragging] = useState(false);
  const [chatResizing, setChatResizing] = useState(false);
  const [playerQuoteBubbles, setPlayerQuoteBubbles] = useState({});
  const [mobileHeaderMenuOpen, setMobileHeaderMenuOpen] = useState(false);
  const [onlineTurnLimitSec, setOnlineTurnLimitSec] = useState(0);
  const [turnTimerNow, setTurnTimerNow] = useState(() => Date.now());
  const [backgroundActivityTick, setBackgroundActivityTick] = useState(0);
  const [isDesktopReveal, setIsDesktopReveal] = useState(
    () => (typeof window === 'undefined' ? true : window.innerWidth > 640)
  );
  const mpRef = useRef(null);
  const onlinePlayerIndexRef = useRef(0);
  const onlinePlayerCountRef = useRef(0);
  const chatOpenRef = useRef(false);
  const chatListRef = useRef(null);
  const chatPanelRef = useRef(null);
  const chatDragRef = useRef(null);
  const chatResizeRef = useRef(null);
  const mobileHeaderMenuRef = useRef(null);
  const localChatIdRef = useRef(1);
  const quoteTimersRef = useRef({});
  const lastQuoteByPlayerRef = useRef({});
  const discardTargetRef = useRef(null);
  const handTargetRef = useRef(null);
  const userHandMainRef = useRef(null);
  const seatPileRefs = useRef([]);
  const userPileDropRefs = useRef([]);
  const ignorePileClickUntilRef = useRef(0);
  const ignoreDiscardClickUntilRef = useRef(0);
  const pendingPickupInsertIndexRef = useRef(null);
  const soundRef = useRef(createSoundManager());
  const prevStateRef = useRef(state);
  const botTurnDeadlineRef = useRef({ key: null, at: 0 });
  const surgeGraceBotDeadlineRef = useRef({ key: null, at: 0 });

  const getTotalCardsForPlayer = useCallback((snapshot, playerIndex) => {
    const handCount = snapshot?.hands?.[playerIndex]?.length ?? 0;
    const pileCount = (snapshot?.handPiles?.[playerIndex] || []).reduce(
      (sum, pile) => sum + (Array.isArray(pile) ? pile.length : 0),
      0
    );
    return handCount + pileCount;
  }, []);

  const themeConfig = THEMES[settings.theme] || THEMES.classic;
  const gameScreenStyle = {
    '--card-back-start': themeConfig.cardBackStart,
    '--card-back-end': themeConfig.cardBackEnd,
  };

  const showTooltip = useCallback((msg, duration = 3000, anchor = 'fan') => {
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
    setTooltipAnchor(anchor);
    setTooltip(msg);
    tooltipTimerRef.current = setTimeout(() => setTooltip(null), duration);
  }, []);

  const showHintOnce = useCallback((key, msg, duration = 3000, anchor = 'fan') => {
    if (tooltipHintsShownRef.current[key]) return;
    tooltipHintsShownRef.current[key] = true;
    showTooltip(msg, duration, anchor);
  }, [showTooltip]);

  const clearPlayerQuoteBubble = useCallback((playerIndex) => {
    const timer = quoteTimersRef.current[playerIndex];
    if (timer) {
      clearTimeout(timer);
      delete quoteTimersRef.current[playerIndex];
    }
    setPlayerQuoteBubbles((prev) => {
      if (!prev[playerIndex]) return prev;
      const next = { ...prev };
      delete next[playerIndex];
      return next;
    });
  }, []);

  const showPlayerQuoteBubble = useCallback((playerIndex, text) => {
    if (!Number.isInteger(playerIndex) || playerIndex < 0 || typeof text !== 'string') return;
    const trimmed = text.trim();
    if (!trimmed) return;
    const timer = quoteTimersRef.current[playerIndex];
    if (timer) clearTimeout(timer);
    setPlayerQuoteBubbles((prev) => ({
      ...prev,
      [playerIndex]: trimmed,
    }));
    quoteTimersRef.current[playerIndex] = setTimeout(() => {
      setPlayerQuoteBubbles((prev) => {
        if (!prev[playerIndex]) return prev;
        const next = { ...prev };
        delete next[playerIndex];
        return next;
      });
      delete quoteTimersRef.current[playerIndex];
    }, PLAYER_QUOTE_BUBBLE_MS);
  }, []);

  useEffect(
    () => () => {
      Object.values(quoteTimersRef.current).forEach((timer) => clearTimeout(timer));
      quoteTimersRef.current = {};
    },
    []
  );

  const isOnline = gameMode === 'online' && Boolean(mpRef.current);

  useEffect(() => {
    chatOpenRef.current = chatOpen;
  }, [chatOpen]);

  useEffect(() => {
    if (!chatOpen || !chatListRef.current) return;
    chatListRef.current.scrollTop = chatListRef.current.scrollHeight;
  }, [chatMessages, chatOpen]);

  useEffect(() => {
    if (chatOpen) setChatUnread(0);
  }, [chatOpen]);

  useEffect(() => {
    const updateViewport = () => {
      const desktop = window.innerWidth > 640;
      setIsDesktopReveal(desktop);
      if (desktop) setMobileHeaderMenuOpen(false);
      if (!desktop) return;
      setChatSize((prev) => {
        if (!prev) return prev;
        const width = Math.min(
          CHAT_PANEL_MAX_WIDTH,
          Math.max(CHAT_PANEL_MIN_WIDTH, Math.min(prev.width, window.innerWidth - CHAT_PANEL_MARGIN * 2))
        );
        const height = Math.min(
          CHAT_PANEL_MAX_HEIGHT,
          Math.max(CHAT_PANEL_MIN_HEIGHT, Math.min(prev.height, window.innerHeight - CHAT_PANEL_MARGIN * 2))
        );
        if (width === prev.width && height === prev.height) return prev;
        return { width, height };
      });
      setChatPosition((prev) => {
        if (!prev) return prev;
        const width = chatPanelRef.current?.offsetWidth ?? 460;
        const height = chatPanelRef.current?.offsetHeight ?? 560;
        const maxX = Math.max(CHAT_PANEL_MARGIN, window.innerWidth - width - CHAT_PANEL_MARGIN);
        const maxY = Math.max(CHAT_PANEL_MARGIN, window.innerHeight - height - CHAT_PANEL_MARGIN);
        const x = Math.min(maxX, Math.max(CHAT_PANEL_MARGIN, prev.x));
        const y = Math.min(maxY, Math.max(CHAT_PANEL_MARGIN, prev.y));
        if (x === prev.x && y === prev.y) return prev;
        return { x, y };
      });
    };
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  useEffect(() => {
    if (!mobileHeaderMenuOpen) return undefined;
    const onPointerDown = (event) => {
      const root = mobileHeaderMenuRef.current;
      if (!root || root.contains(event.target)) return;
      setMobileHeaderMenuOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [mobileHeaderMenuOpen]);

  useEffect(() => {
    if (!flyingCard || !document.hidden) return;
    setFlyingCard((prev) => {
      if (!prev || !document.hidden) return prev;
      prev?.onComplete?.(prev.card);
      return null;
    });
  }, [flyingCard]);

  useEffect(() => {
    const pulse = () => {
      setTurnTimerNow(Date.now());
      setSurgeGraceNow(Date.now());
      setBackgroundActivityTick((n) => n + 1);
    };
    const onVisibilityChange = () => {
      if (document.hidden) {
        // Browsers throttle/stop animations in hidden tabs; complete in-flight card actions now.
        setFlyingCard((prev) => {
          if (!prev) return null;
          prev?.onComplete?.(prev.card);
          return null;
        });
      }
      pulse();
    };
    window.addEventListener('focus', pulse);
    window.addEventListener('pageshow', pulse);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('focus', pulse);
      window.removeEventListener('pageshow', pulse);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const gid = state.gameId || null;
    if (!gid || gid === tooltipHintsGameIdRef.current) return;
    tooltipHintsGameIdRef.current = gid;
    tooltipHintsShownRef.current = { discard: false, pile: false, pickupRule: false, privatePile: false };
    setTooltip(null);
  }, [state.gameId]);

  const appendChatMessage = useCallback((message) => {
    if (!message || typeof message.text !== 'string') return;
    setChatMessages((prev) => {
      const next = [...prev, message];
      if (next.length > CHAT_MAX_MESSAGES) return next.slice(next.length - CHAT_MAX_MESSAGES);
      return next;
    });
    if (!chatOpenRef.current) {
      setChatUnread((u) => u + 1);
    }
  }, []);

  const connectToRoom = useCallback((code, name) => {
    if (mpRef.current) mpRef.current.disconnect();
    setOnlineError(null);
    const conn = createConnection({
      roomId: code,
      playerName: name || playerNames[0] || 'Player',
      onLobbyUpdate: (data) => setOnlineLobby(data),
      onStateUpdate: (data) => {
        setOnlineState(data.state);
        setOnlinePlayerIndex(data.playerIndex);
        onlinePlayerIndexRef.current = data.playerIndex;
        const s = data.state;
        onlinePlayerCountRef.current = s?.numPlayers ?? 0;
        const me = data.playerIndex;
        const n = s.numPlayers;
        if (s.phase === 'playing' || s.phase === 'surgeGrace' || s.phase === 'roundEnd' || s.phase === 'gameOver') {
          const remap = (i) => (i + me) % n;
          const unmap = (i) => (i - me + n) % n;

          const remappedNames = Array.from({ length: n }, (_, i) => s.playerNames[remap(i)]);
          const remappedHands = Array.from({ length: n }, (_, i) => {
            const serverIdx = remap(i);
            if (serverIdx === me) return s.myHand;
            const revealHand = Array.isArray(s.revealHands?.[serverIdx]) ? s.revealHands[serverIdx] : null;
            if (revealHand) return revealHand;
            const count = s.handCounts?.[serverIdx] ?? 0;
            return Array.from({ length: count }, (__, ci) => ({ id: `${serverIdx}-back-${ci}`, suit: 's', rank: 1, isJoker: false }));
          });
          const remappedPiles = Array.from({ length: n }, (_, i) => {
            const serverIdx = remap(i);
            if (serverIdx === me) return s.myPiles;
            const revealPiles = Array.isArray(s.revealPiles?.[serverIdx]) ? s.revealPiles[serverIdx] : null;
            if (revealPiles) return revealPiles;
            return (s.handPiles[serverIdx] || []).map((pile) =>
              pile.map((c, ci) => c || { id: `pile-${serverIdx}-${ci}`, suit: 's', rank: 1, isJoker: false })
            );
          });
          const remappedScores = Array.from({ length: n }, (_, i) => s.gameScores[remap(i)]);
          const remappedRoundScores = Array.from({ length: n }, (_, i) => s.roundScores[remap(i)]);

          const serverCurrentPlayer = s.currentPlayerIndex;
          const localCurrentPlayer = unmap(serverCurrentPlayer);

          setState((prev) => ({
            ...prev,
            phase: s.phase === 'playing' ? PHASES.PLAYING
              : s.phase === 'surgeGrace' ? PHASES.SURGE_GRACE
              : s.phase === 'roundEnd' ? PHASES.ROUND_END
              : PHASES.GAME_OVER,
            gameId: s.gameId || prev.gameId,
            numPlayers: n,
            playerNames: remappedNames,
            hands: remappedHands,
            handPiles: remappedPiles,
            discardPile: s.discardPile,
            startPlayerIndex: unmap(s.startPlayerIndex ?? 0),
            currentPlayerIndex: localCurrentPlayer,
            turnTimeLimitSec: s.turnTimeLimitSec ?? null,
            turnDeadlineAt: s.turnDeadlineAt ?? null,
            lastPickedUpCardId: s.lastPickedUpCardId,
            roundNumber: s.roundNumber,
            roundScores: remappedRoundScores,
            roundDetails: Array.isArray(s.roundDetails)
              ? Array.from({ length: n }, (_, i) => s.roundDetails[remap(i)])
              : s.roundDetails,
            gameScores: remappedScores,
            sergCalled: s.sergCalled,
            surgeGraceEndsAt: s.surgeGraceEndsAt ?? null,
            readyPlayers: Array.isArray(s.readyPlayers)
              ? Array.from({ length: n }, (_, i) => Boolean(s.readyPlayers[remap(i)]))
              : null,
            gameWinner: s.gameWinner != null ? unmap(s.gameWinner) : null,
            roundWinner: null,
          }));
        }
      },
      onChatMessage: (message) => {
        appendChatMessage(message);
      },
      onMartiniQuote: (message) => {
        if (!message || !Number.isInteger(message.playerIndex) || typeof message.text !== 'string') return;
        const n = onlinePlayerCountRef.current;
        const me = onlinePlayerIndexRef.current ?? 0;
        if (n < 2) return;
        const localIndex = (message.playerIndex - me + n) % n;
        showPlayerQuoteBubble(localIndex, message.text);
      },
      onChatHistory: (messages) => {
        const cleaned = Array.isArray(messages) ? messages.slice(-CHAT_MAX_MESSAGES) : [];
        setChatMessages(cleaned);
      },
      onChatReset: () => {
        setChatMessages([]);
        setChatUnread(0);
      },
      onError: (msg) => setOnlineError(msg),
    });
    mpRef.current = conn;
    setRoomCode(code);
  }, [appendChatMessage, playerNames, showPlayerQuoteBubble]);

  const handleCreateRoom = useCallback(() => {
    const code = generateRoomCode();
    connectToRoom(code, playerNames[0]);
  }, [connectToRoom, playerNames]);

  const handleJoinRoom = useCallback(() => {
    if (joinCode.length < 4) return;
    connectToRoom(joinCode.toUpperCase(), playerNames[0]);
  }, [connectToRoom, joinCode, playerNames]);

  const handleLeaveRoom = useCallback((options = {}) => {
    const { notifyServer = true } = options;
    const conn = mpRef.current;
    if (conn) {
      if (notifyServer) {
        conn.leaveRoom?.();
        window.setTimeout(() => {
          conn.disconnect();
        }, 40);
      } else {
        conn.disconnect();
      }
    }
    mpRef.current = null;
    onlinePlayerIndexRef.current = 0;
    onlinePlayerCountRef.current = 0;
    setOnlineLobby(null);
    setOnlineState(null);
    setRoomCode('');
    setOnlineError(null);
    setChatMessages([]);
    setChatUnread(0);
    setChatDraft('');
    setChatOpen(false);
    setChatPosition(null);
    setChatSize(null);
    setChatDragging(false);
    setChatResizing(false);
    pendingPickupInsertIndexRef.current = null;
    Object.values(quoteTimersRef.current).forEach((timer) => clearTimeout(timer));
    quoteTimersRef.current = {};
    lastQuoteByPlayerRef.current = {};
    setPlayerQuoteBubbles({});
  }, []);

  const handleToggleChat = useCallback(() => {
    setChatOpen((open) => {
      const next = !open;
      if (next) setChatUnread(0);
      return next;
    });
  }, []);

  const handleChatHeaderPointerDown = useCallback((e) => {
    if (!isDesktopReveal) return;
    if (chatResizing) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const panel = chatPanelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    chatDragRef.current = {
      pointerId: e.pointerId,
      startX,
      startY,
      originX: rect.left,
      originY: rect.top,
      width: rect.width,
      height: rect.height,
    };
    setChatPosition({ x: rect.left, y: rect.top });
    setChatDragging(true);

    const onPointerMove = (ev) => {
      const drag = chatDragRef.current;
      if (!drag || ev.pointerId !== drag.pointerId) return;
      const dx = ev.clientX - drag.startX;
      const dy = ev.clientY - drag.startY;
      const maxX = Math.max(CHAT_PANEL_MARGIN, window.innerWidth - drag.width - CHAT_PANEL_MARGIN);
      const maxY = Math.max(CHAT_PANEL_MARGIN, window.innerHeight - drag.height - CHAT_PANEL_MARGIN);
      const nextX = Math.min(maxX, Math.max(CHAT_PANEL_MARGIN, drag.originX + dx));
      const nextY = Math.min(maxY, Math.max(CHAT_PANEL_MARGIN, drag.originY + dy));
      setChatPosition({ x: nextX, y: nextY });
    };

    const onPointerEnd = (ev) => {
      const drag = chatDragRef.current;
      if (!drag || ev.pointerId !== drag.pointerId) return;
      chatDragRef.current = null;
      setChatDragging(false);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerEnd);
      window.removeEventListener('pointercancel', onPointerEnd);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerEnd);
    window.addEventListener('pointercancel', onPointerEnd);
  }, [chatResizing, isDesktopReveal]);

  const handleChatResizePointerDown = useCallback((mode) => (e) => {
    if (!isDesktopReveal) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const panel = chatPanelRef.current;
    if (!panel) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = panel.getBoundingClientRect();
    const minW = CHAT_PANEL_MIN_WIDTH;
    const minH = CHAT_PANEL_MIN_HEIGHT;
    chatResizeRef.current = {
      pointerId: e.pointerId,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      originX: rect.left,
      originY: rect.top,
      originW: rect.width,
      originH: rect.height,
      minW,
      minH,
    };
    setChatPosition({ x: rect.left, y: rect.top });
    setChatSize({ width: rect.width, height: rect.height });
    setChatResizing(true);

    const onPointerMove = (ev) => {
      const rs = chatResizeRef.current;
      if (!rs || ev.pointerId !== rs.pointerId) return;
      const dx = ev.clientX - rs.startX;
      const dy = ev.clientY - rs.startY;
      const maxWByViewport = Math.max(rs.minW, window.innerWidth - CHAT_PANEL_MARGIN * 2);
      const maxHByViewport = Math.max(rs.minH, window.innerHeight - CHAT_PANEL_MARGIN * 2);
      const maxWByPosition = Math.max(rs.minW, window.innerWidth - rs.originX - CHAT_PANEL_MARGIN);
      const maxHByPosition = Math.max(rs.minH, window.innerHeight - rs.originY - CHAT_PANEL_MARGIN);
      const maxW = Math.min(CHAT_PANEL_MAX_WIDTH, maxWByViewport, maxWByPosition);
      const maxH = Math.min(CHAT_PANEL_MAX_HEIGHT, maxHByViewport, maxHByPosition);

      if (rs.mode === 'corner') {
        const width = Math.min(maxW, Math.max(rs.minW, rs.originW + dx));
        const height = Math.min(maxH, Math.max(rs.minH, rs.originH + dy));
        setChatSize({ width, height });
        return;
      }

      if (rs.mode === 'top') {
        const maxTopResizeHeight = Math.min(
          CHAT_PANEL_MAX_HEIGHT,
          maxHByViewport,
          Math.max(rs.minH, rs.originY + rs.originH - CHAT_PANEL_MARGIN)
        );
        const nextHeight = Math.min(maxTopResizeHeight, Math.max(rs.minH, rs.originH - dy));
        const nextYRaw = rs.originY + (rs.originH - nextHeight);
        const nextY = Math.min(
          window.innerHeight - nextHeight - CHAT_PANEL_MARGIN,
          Math.max(CHAT_PANEL_MARGIN, nextYRaw)
        );
        setChatSize((prev) => ({ width: prev?.width ?? rs.originW, height: nextHeight }));
        setChatPosition((prev) => ({ x: prev?.x ?? rs.originX, y: nextY }));
      }
    };

    const onPointerEnd = (ev) => {
      const rs = chatResizeRef.current;
      if (!rs || ev.pointerId !== rs.pointerId) return;
      chatResizeRef.current = null;
      setChatResizing(false);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerEnd);
      window.removeEventListener('pointercancel', onPointerEnd);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerEnd);
    window.addEventListener('pointercancel', onPointerEnd);
  }, [isDesktopReveal]);

  const handleSendChat = useCallback(() => {
    const text = chatDraft.trim();
    if (!text) return;
    if (gameMode === 'online') {
      if (!mpRef.current) return;
      mpRef.current.chat(text);
    } else {
      const now = Date.now();
      appendChatMessage({
        id: `local-${localChatIdRef.current++}`,
        playerIndex: 0,
        playerName: state.playerNames?.[0] || 'You',
        text,
        timestamp: now,
      });
      const botNames = (state.playerNames || []).slice(1);
      if (botNames.length > 0) {
        const replyName = botNames[Math.floor(Math.random() * botNames.length)];
        const replyText = BOT_CHAT_REPLIES[Math.floor(Math.random() * BOT_CHAT_REPLIES.length)];
        window.setTimeout(() => {
          appendChatMessage({
            id: `local-${localChatIdRef.current++}`,
            playerIndex: 1,
            playerName: replyName,
            text: replyText,
            timestamp: Date.now(),
          });
        }, 500 + Math.floor(Math.random() * 800));
      }
    }
    setChatDraft('');
  }, [appendChatMessage, chatDraft, gameMode, state.playerNames]);

  useEffect(() => {
    return () => {
      if (mpRef.current) mpRef.current.disconnect();
    };
  }, []);

  useEffect(() => {
    setPlayerNames((prev) => normalizePlayerNames(playerCount, prev));
  }, [playerCount]);

  useEffect(() => {
    if (state.phase === PHASES.LOBBY) return;
    setPlayerCount(state.numPlayers);
    setPlayerNames(normalizePlayerNames(state.numPlayers, state.playerNames));
    if (state.botDifficulty && state.botDifficulty !== settings.botDifficulty) {
      setSettings((prev) => ({ ...prev, botDifficulty: state.botDifficulty }));
    }
  }, [state.phase, state.numPlayers, state.playerNames, state.botDifficulty, settings.botDifficulty]);

  useEffect(() => {
    if (state.phase === PHASES.LOBBY) {
      Object.keys(quoteTimersRef.current).forEach((idx) => clearPlayerQuoteBubble(Number(idx)));
      lastQuoteByPlayerRef.current = {};
      return;
    }
    setPlayerQuoteBubbles((prev) => {
      const next = {};
      let changed = false;
      Object.entries(prev).forEach(([idx, text]) => {
        const numIdx = Number(idx);
        if (numIdx >= 0 && numIdx < state.numPlayers) {
          next[numIdx] = text;
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [clearPlayerQuoteBubble, state.phase, state.numPlayers]);

  useEffect(() => {
    soundRef.current.setMuted(settings.soundMuted);
  }, [settings.soundMuted]);

  useEffect(() => {
    soundRef.current.setAmbienceEnabled(settings.ambienceEnabled);
  }, [settings.ambienceEnabled]);

  useEffect(() => {
    saveStoredData({ state, playerCount, playerNames, settings, stats });
  }, [state, playerCount, playerNames, settings, stats]);

  useEffect(() => {
    if (state.phase !== PHASES.GAME_OVER || !state.gameId) return;
    setStats((prev) => {
      if (prev.lastRecordedGameId === state.gameId) return prev;
      const yourScore = state.gameScores?.[0] ?? 0;
      return {
        ...prev,
        gamesPlayed: prev.gamesPlayed + 1,
        wins: prev.wins + (state.gameWinner === 0 ? 1 : 0),
        highScore: Math.max(prev.highScore, yourScore),
        lastRecordedGameId: state.gameId,
      };
    });
  }, [state.phase, state.gameId, state.gameWinner, state.gameScores]);

  const prevPhaseRef = useRef(state.phase);
  useEffect(() => {
    if (state.phase === PHASES.ROUND_END && prevPhaseRef.current !== PHASES.ROUND_END) {
      soundRef.current.playRoundEnd();
    }
    if (state.phase === PHASES.GAME_OVER && prevPhaseRef.current !== PHASES.GAME_OVER) {
      soundRef.current.playGameEnd();
    }
    prevPhaseRef.current = state.phase;
  }, [state.phase]);

  useEffect(() => {
    if (state.phase !== PHASES.GAME_OVER) {
      setGameWinnerRevealStep(0);
      return undefined;
    }
    setGameWinnerRevealStep(1);
    const t1 = window.setTimeout(() => setGameWinnerRevealStep(2), 900);
    const t2 = window.setTimeout(() => setGameWinnerRevealStep(0), 2500);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [state.phase, state.gameWinner, state.gameId]);

  useEffect(() => {
    const prev = prevStateRef.current;
    if (state.phase !== PHASES.SURGE_GRACE || prev?.phase === PHASES.SURGE_GRACE) return undefined;
    const callerIndex = state.currentPlayerIndex ?? 0;
    const callerName = state.playerNames?.[callerIndex] || 'Player';
    setSurgeCallSplash({ callerName, key: `${state.gameId || 'g'}-${Date.now()}` });
    const timer = window.setTimeout(() => setSurgeCallSplash(null), 1900);
    return () => window.clearTimeout(timer);
  }, [state.phase, state.currentPlayerIndex, state.playerNames, state.gameId]);

  useEffect(() => {
    if (state.phase !== PHASES.ROUND_END && state.phase !== PHASES.GAME_OVER) return;
    const key = `${state.gameId}-${state.roundNumber}-${state.phase}`;
    if (animatedRoundKeyRef.current === key) return;
    animatedRoundKeyRef.current = key;
    const userDetail = state.roundDetails?.[0];
    const prevState = prevStateRef.current;
    const finalPickupPhases = (() => {
      if (!prevState || prevState.phase !== PHASES.SURGE_GRACE) return [];
      if (!prevState.discardPile) return [];
      const receiverIndex = prevState.hands.findIndex((_, i) => getTotalCardsForPlayer(prevState, i) === 9);
      if (receiverIndex < 0) return [];
      const hasEleven = prevState.hands.some((_, i) => getTotalCardsForPlayer(prevState, i) === 11);
      if (hasEleven) return [];
      return [{ type: 'finalPickup', index: receiverIndex, card: prevState.discardPile }];
    })();
    const opponentRevealPhases = state.playerNames
      .map((_, i) => i)
      .filter((i) => i !== 0)
      .flatMap((i) => {
        if (!isDesktopReveal) return [{ type: 'otherTotal', index: i }];
        const revealPiles = (state.roundDetails?.[i]?.pileScores || [])
          .filter((p) => Array.isArray(p?.cards) && p.cards.length > 0);
        return [
          { type: 'otherReveal', index: i },
          ...revealPiles.map((_, pileIndex) => ({ type: 'otherRevealPile', index: i, pileIndex })),
          { type: 'otherTotal', index: i },
        ];
      });
    const phases = [
      ...finalPickupPhases,
      ...((userDetail?.pileScores || []).map((_, i) => ({ type: 'pile', index: i }))),
      ...(userDetail?.penalty > 0 ? [{ type: 'penalty' }] : []),
      ...(userDetail?.roundShellBonus > 0 ? [{ type: 'bonus' }] : []),
      { type: 'fly' },
      ...(Number.isFinite(userDetail?.maxPotential) ? [{ type: 'max' }] : []),
      ...opponentRevealPhases,
      ...(state.phase === PHASES.ROUND_END ? [{ type: 'roundSplash', nextRound: (state.roundNumber ?? 1) + 1 }] : []),
    ];
    setSurgeReveal({
      pending: {
        roundScores: state.roundScores,
        gameScores: state.gameScores,
        roundDetails: state.roundDetails,
      },
      phaseIndex: 0,
      phases: phases.length > 0 ? phases : [{ type: 'fly' }],
      postAction: state.phase === PHASES.ROUND_END ? 'nextRound' : 'none',
    });
  }, [state.phase, state.gameId, state.roundNumber, state.roundDetails, state.roundScores, state.gameScores, state.playerNames, isDesktopReveal, getTotalCardsForPlayer]);

  useEffect(() => {
    prevStateRef.current = state;
  }, [state]);

  const handleStart = useCallback(() => {
    const normalizedNames = normalizePlayerNames(playerCount, playerNames);
    soundRef.current.unlock();
    setPlayerNames(normalizedNames);
    setState(
      startGame(playerCount, {
        playerNames: normalizedNames,
        botDifficulty: settings.botDifficulty,
      })
    );
  }, [playerCount, playerNames, settings.botDifficulty]);

  const handleOpenQuickStart = useCallback((action) => {
    if (!action) return;
    soundRef.current.unlock();
    if (action === 'bots') {
      handleStart();
    } else if (action === 'online') {
      mpRef.current?.startGame({ turnTimeLimitSec: onlineTurnLimitSec });
    }
    setShowQuickStartOverlay(true);
  }, [handleStart, onlineTurnLimitSec]);

  const handleCloseQuickStart = useCallback(() => {
    setShowQuickStartOverlay(false);
  }, []);

  const resetToLobby = (sourceState = state) => {
    const count = sourceState.numPlayers ?? playerCount;
    const names = normalizePlayerNames(count, sourceState.playerNames ?? playerNames);
    setPlayerCount(count);
    setPlayerNames(names);
    setHandSelectedIds([]);
    setShowRulesOverlay(false);
    setShowPointsOverlay(false);
    setState(
      initialGameState(count, {
        playerNames: names,
        botDifficulty: sourceState.botDifficulty || settings.botDifficulty,
      })
    );
  };

  const handleLobbyClick = () => {
    soundRef.current.unlock();
    if (state.phase === PHASES.LOBBY) return;
    setShowLobbyConfirm(true);
  };

  const handleConfirmLobby = () => {
    const source = state;
    if (gameMode === 'online') {
      handleLeaveRoom({ notifyServer: true });
    }
    resetToLobby(source);
    clearStoredActiveGame({
      playerCount: source.numPlayers ?? playerCount,
      playerNames: normalizePlayerNames(source.numPlayers ?? playerCount, source.playerNames ?? playerNames),
      settings,
      stats,
    });
    setShowLobbyConfirm(false);
  };

  const handleCancelLobby = () => {
    setShowLobbyConfirm(false);
  };

  const getPlayerTotalCards = useCallback(
    (s, playerIndex) =>
      (s.hands?.[playerIndex]?.length ?? 0) +
      (s.handPiles?.[playerIndex] || []).reduce(
        (sum, pile) => sum + (Array.isArray(pile) ? pile.length : 0),
        0
      ),
    []
  );

  const handleDiscard = useCallback((playerIndex, card) => {
    if (mpRef.current && gameMode === 'online') {
      if (state.phase === PHASES.SURGE_GRACE && getPlayerTotalCards(state, playerIndex) === 11) {
        mpRef.current.surgeEqualizeDiscard(card.id);
      } else {
        mpRef.current.discard(card.id);
      }
    } else {
      setState((s) => {
        if (s.phase === PHASES.SURGE_GRACE && getPlayerTotalCards(s, playerIndex) === 11) {
          return surgeGraceEqualizeDiscard(s, playerIndex, card);
        }
        return discardCard(s, playerIndex, card);
      });
    }
  }, [gameMode, state, getPlayerTotalCards]);

  const handlePickUpFromPile = useCallback((playerIndex) => {
    if (mpRef.current && gameMode === 'online') {
      mpRef.current.pickUp();
    } else {
      setState((s) => pickUpFromPile(s, playerIndex));
    }
  }, [gameMode]);

  const handleSerg = useCallback(() => {
    if (dealAnimatingRef.current) return;
    if (surgeReveal) return;
    if (state.phase !== PHASES.PLAYING) return;
    soundRef.current.playSurg();
    setHandSelectedIds([]);
    if (mpRef.current && gameMode === 'online') {
      mpRef.current.surge();
    } else {
      setState((s) => callSerg(s));
    }
  }, [gameMode, surgeReveal, state.phase]);

  const handleMartiniActivate = useCallback(() => {
    if (state.phase !== PHASES.PLAYING && state.phase !== PHASES.SURGE_GRACE) return;
    if (mpRef.current && gameMode === 'online') {
      mpRef.current.martiniQuote?.();
      return;
    }
    const next = pickBondQuote(lastQuoteByPlayerRef.current[0]);
    lastQuoteByPlayerRef.current[0] = next;
    showPlayerQuoteBubble(0, next);
  }, [gameMode, state.phase, showPlayerQuoteBubble]);

  const handleSurgeReady = useCallback(() => {
    if (state.phase !== PHASES.SURGE_GRACE) return;
    const myTotalCards = (state.hands?.[0]?.length ?? 0) + (state.handPiles?.[0] || []).reduce(
      (sum, pile) => sum + (Array.isArray(pile) ? pile.length : 0),
      0
    );
    if (myTotalCards !== 10) return;
    if (mpRef.current && gameMode === 'online') {
      mpRef.current.ready();
      return;
    }
    setState((s) => {
      const next = setSurgeReady(s, 0);
      if (Array.isArray(next.readyPlayers) && next.readyPlayers.every(Boolean)) {
        return finalizeSerg(next);
      }
      return next;
    });
  }, [gameMode, state.phase, state.hands, state.handPiles]);

  const handleReorder = useCallback((fromIndex, toIndex) => {
    if (dealAnimatingRef.current) return;
    setState((s) => reorderHand(s, 0, fromIndex, toIndex));
    if (mpRef.current && gameMode === 'online') {
      mpRef.current.reorder(fromIndex, toIndex);
    }
  }, [gameMode]);

  useEffect(() => {
    if (gameMode !== 'online') return;
    const pendingTo = pendingPickupInsertIndexRef.current;
    if (!Number.isFinite(pendingTo)) return;
    if (!state.lastPickedUpCardId) return;
    const hand = state.hands?.[0] || [];
    if (hand.length < 1) return;
    const from = hand.findIndex((c) => c.id === state.lastPickedUpCardId);
    if (from < 0) return;
    const to = Math.max(0, Math.min(pendingTo, hand.length - 1));
    pendingPickupInsertIndexRef.current = null;
    if (from !== to) handleReorder(from, to);
  }, [gameMode, state.hands, state.lastPickedUpCardId, handleReorder]);

  const handleFlyingCardComplete = useCallback((card) => {
    setFlyingCard((prev) => {
      if (prev?.sound === 'pickup') {
        soundRef.current.playCardPickup();
      }
      if (prev?.sound === 'discard') {
        soundRef.current.playCardPlace();
      }
      const next = prev?.onComplete?.(card);
      return next ?? null;
    });
  }, []);

  const getCenter = (el) => {
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  };

  const getHandCardCenter = (cardId) => {
    if (!cardId) return null;
    const el = document.querySelector(`.hand-fan [data-hand-card-id="${cardId}"]`);
    return getCenter(el);
  };

  const playerIndexToSeatIndex = useMemo(() => {
    const prefix = 'poker-table__seat--';
    const slotByCount = {
      2: [prefix + 'user', prefix + 'top-center'],
      3: [prefix + 'user', prefix + 'top-1-4', prefix + 'top-3-4'],
      4: [prefix + 'user', prefix + 'top-center', prefix + 'left-middle', prefix + 'right-middle'],
      5: [prefix + 'user', prefix + 'top-1-4', prefix + 'top-3-4', prefix + 'left-middle', prefix + 'right-middle'],
    };
    const clockwiseOrder = [prefix + 'left-middle', prefix + 'top-1-4', prefix + 'top-center', prefix + 'top-3-4', prefix + 'right-middle'];
    const slotClasses = slotByCount[state.numPlayers] || slotByCount[2];
    const opponentClasses = slotClasses.slice(1);
    const sortedByClockwise = [...opponentClasses].sort(
      (a, b) => clockwiseOrder.indexOf(a) - clockwiseOrder.indexOf(b)
    );
    const map = { 0: 0 };
    for (let p = 1; p < state.numPlayers; p += 1) {
      const slotClass = sortedByClockwise[p - 1];
      const seatIdx = slotClasses.indexOf(slotClass);
      map[p] = seatIdx >= 0 ? seatIdx : p;
    }
    return map;
  }, [state.numPlayers]);

  const getSeatPileEl = useCallback(
    (playerIndex) =>
      seatPileRefs.current?.[playerIndexToSeatIndex[playerIndex] ?? playerIndex] ?? null,
    [playerIndexToSeatIndex]
  );

  const buildDealFlightForStep = useCallback(
    (step) => {
      const startPos = getCenter(discardTargetRef.current);
      if (!startPos) return null;
      const n = state.numPlayers;
      const playerIndex = step % n;
      const targetEl = playerIndex === 0 ? handTargetRef.current : getSeatPileEl(playerIndex);
      const targetPos = getCenter(targetEl);
      if (!targetPos) return null;
      return {
        id: `deal-${step}`,
        card: { id: `deal-${step}`, rank: 1, suit: 's', isJoker: false },
        startPos,
        targetPos,
      };
    },
    [state.numPlayers, getSeatPileEl]
  );

  const startDealSequence = useCallback((dealKey) => {
    const n = state.numPlayers;
    const totalSteps = 10 * n;
    setDealDealtCounts(Array(n).fill(0));
    setDealAnimation({
      dealKey,
      step: 0,
      totalSteps,
      flight: null,
    });
  }, [state.numPlayers]);

  const handleDealFlightComplete = useCallback(() => {
    const landedStep = dealFlightStepRef.current;
    const n = state.numPlayers;
    soundRef.current.playDealTick();
    setDealDealtCounts((c) => {
      if (!c || c.length !== n) return c;
      const nx = [...c];
      nx[landedStep % n] += 1;
      const totalPlaced = nx.reduce((a, b) => a + b, 0);
      if (totalPlaced >= 10 * n) return null;
      return nx;
    });
    setDealAnimation((prev) => {
      if (!prev?.flight) return null;
      const nextStep = prev.step + 1;
      if (nextStep >= prev.totalSteps) {
        finishedDealKeysRef.current.add(prev.dealKey);
        activeDealKeyRef.current = null;
        return null;
      }
      const built = buildDealFlightForStep(nextStep);
      if (!built) {
        finishedDealKeysRef.current.add(prev.dealKey);
        activeDealKeyRef.current = null;
        return null;
      }
      return {
        dealKey: prev.dealKey,
        step: nextStep,
        totalSteps: prev.totalSteps,
        flight: built,
      };
    });
  }, [buildDealFlightForStep, state.numPlayers]);

  /** Measure deck + seats after the centered deck mounts, then start the first flight. */
  useLayoutEffect(() => {
    if (!dealAnimation || dealAnimation.flight !== null) return;
    const { step, totalSteps, dealKey } = dealAnimation;
    if (step >= totalSteps) {
      activeDealKeyRef.current = null;
      setDealDealtCounts(null);
      setDealAnimation(null);
      return;
    }
    const built = buildDealFlightForStep(step);
    if (!built) {
      finishedDealKeysRef.current.add(dealKey);
      activeDealKeyRef.current = null;
      setDealDealtCounts(null);
      setDealAnimation(null);
      return;
    }
    setDealAnimation((d) => (d && d.flight === null && d.dealKey === dealKey ? { ...d, flight: built } : d));
  }, [dealAnimation, buildDealFlightForStep]);

  useEffect(() => {
    if (state.phase !== PHASES.PLAYING) return undefined;
    const key = `${state.gameId}-${state.roundNumber}`;
    if (skipDealAnimationRef.current) {
      skipDealAnimationRef.current = false;
      finishedDealKeysRef.current.add(key);
      return undefined;
    }
    if (finishedDealKeysRef.current.has(key)) return undefined;
    if (activeDealKeyRef.current === key) return undefined;

    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      if (activeDealKeyRef.current === key) return;
      activeDealKeyRef.current = key;
      soundRef.current.unlock();
      startDealSequence(key);
    };
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
      if (activeDealKeyRef.current === key) activeDealKeyRef.current = null;
      setDealDealtCounts(null);
      setDealAnimation((d) => (d?.dealKey === key ? null : d));
    };
  }, [state.phase, state.gameId, state.roundNumber, gameMode, startDealSequence]);

  useEffect(() => {
    if (gameMode !== 'online') return undefined;
    if (state.phase !== PHASES.PLAYING) return undefined;
    if (!state.turnDeadlineAt || !state.turnTimeLimitSec) return undefined;
    const tick = () => setTurnTimerNow(Date.now());
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [gameMode, state.phase, state.turnDeadlineAt, state.turnTimeLimitSec]);

  useEffect(() => {
    if (state.phase !== PHASES.SURGE_GRACE) return undefined;
    if (!state.surgeGraceEndsAt) return undefined;

    const tick = () => setSurgeGraceNow(Date.now());
    tick();
    const intervalId = window.setInterval(tick, 250);
    const remaining = Math.max(0, state.surgeGraceEndsAt - Date.now());
    const timeoutId = window.setTimeout(() => {
      if (gameMode === 'online') return;
      setState((s) => finalizeSerg(s));
    }, remaining + 10);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [state.phase, state.surgeGraceEndsAt, gameMode]);

  useEffect(() => {
    if (gameMode !== 'bots') return undefined;
    if (state.phase !== PHASES.SURGE_GRACE) return undefined;
    const timeouts = [];
    for (let p = 1; p < state.numPlayers; p += 1) {
      const id = window.setTimeout(() => {
        setState((s) => {
          const next = setSurgeReady(s, p);
          if (Array.isArray(next.readyPlayers) && next.readyPlayers.every(Boolean)) {
            return finalizeSerg(next);
          }
          return next;
        });
      }, 700 + p * 450);
      timeouts.push(id);
    }
    return () => {
      timeouts.forEach((id) => window.clearTimeout(id));
    };
  }, [gameMode, state.phase, state.numPlayers]);

  const userHandDealPlaceholders = useMemo(() => {
    if (!dealDealtCounts) return null;
    const k = dealDealtCounts[0] ?? 0;
    return Array.from({ length: k }, (_, i) => ({
      id: `deal-ph-${i}`,
      rank: 1,
      suit: 's',
      isJoker: false,
    }));
  }, [dealDealtCounts]);

  /** Advance SURGE reveal phases; last step commits `pending` state. */
  useEffect(() => {
    if (!surgeReveal) return undefined;
    const { phaseIndex, phases } = surgeReveal;
    const phase = phases[phaseIndex];
    if (!phase) return undefined;
    const isLast = phaseIndex === phases.length - 1;
    const delay =
      phase.type === 'finalPickup'
        ? 900
        :
      phase.type === 'fly'
        ? 1500
        : phase.type === 'penalty'
          ? 1150
          : phase.type === 'bonus'
            ? 1100
        : phase.type === 'max'
          ? 1300
          : phase.type === 'otherReveal'
            ? 550
          : phase.type === 'otherRevealPile'
            ? 900
          : phase.type === 'otherTotal'
            ? 1700
            : phase.type === 'roundSplash'
              ? 1450
              : 1200;
    const id = window.setTimeout(() => {
      if (isLast) {
        setSurgeReveal(null);
        setSurgeFlyScore(null);
        if (surgeReveal.postAction === 'nextRound') {
          setTimeout(() => {
            setHandSelectedIds([]);
            if (mpRef.current && gameMode === 'online') {
              mpRef.current.nextRound();
            } else {
              setState((s) => nextRound(s));
            }
          }, 220);
        }
      } else {
        setSurgeReveal((sr) => (sr ? { ...sr, phaseIndex: sr.phaseIndex + 1 } : null));
      }
    }, delay);
    return () => window.clearTimeout(id);
  }, [surgeReveal, gameMode]);

  useEffect(() => {
    if (!surgeReveal) return;
    const phase = surgeReveal.phases[surgeReveal.phaseIndex];
    if (phase?.type !== 'finalPickup') return;
    const startPos = getCenter(discardTargetRef.current);
    const targetEl = phase.index === 0 ? handTargetRef.current : getSeatPileEl(phase.index);
    const targetPos = getCenter(targetEl);
    if (!startPos || !targetPos || !phase.card) return;
    setFlyingCard({
      card: phase.card,
      startPos,
      targetPos,
      sound: 'pickup',
      onComplete: () => {},
    });
  }, [surgeReveal, getSeatPileEl]);

  /** Animate user total during fly phase (old/new totals from pending round, not live state). */
  useEffect(() => {
    if (!surgeReveal) {
      setSurgeFlyScore(null);
      return undefined;
    }
    const ph = surgeReveal.phases[surgeReveal.phaseIndex];
    if (ph?.type !== 'fly') return undefined;
    const rs = surgeReveal.pending.roundScores?.[0] ?? 0;
    const newS = surgeReveal.pending.gameScores[0] ?? 0;
    const oldS = newS - rs;
    let raf;
    let t0;
    const dur = 1200;
    const tick = (ts) => {
      if (t0 === undefined) t0 = ts;
      const u = Math.min(1, (ts - t0) / dur);
      const eased = 1 - (1 - u) ** 3;
      setSurgeFlyScore(Math.round(oldS + (newS - oldS) * eased));
      if (u < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [surgeReveal]);

  useEffect(() => {
    if (!surgeReveal) {
      setSurgeOtherFlyScore(null);
      return undefined;
    }
    const ph = surgeReveal.phases[surgeReveal.phaseIndex];
    if (ph?.type !== 'otherTotal') {
      setSurgeOtherFlyScore(null);
      return undefined;
    }
    const idx = ph.index;
    const rs = surgeReveal.pending.roundScores?.[idx] ?? 0;
    const newS = surgeReveal.pending.gameScores?.[idx] ?? 0;
    const oldS = newS - rs;
    let raf;
    let t0;
    const dur = 1600;
    const tick = (ts) => {
      if (t0 === undefined) t0 = ts;
      const u = Math.min(1, (ts - t0) / dur);
      setSurgeOtherFlyScore({
        index: idx,
        value: Math.round(oldS + (newS - oldS) * u),
      });
      if (u < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [surgeReveal]);

  const handlePickUpClick = useCallback(() => {
    if (dealAnimatingRef.current) return;
    if (surgeReveal) return;
    if (!state.discardPile) return;
    const startPos = getCenter(discardTargetRef.current);
    const targetPos = getCenter(handTargetRef.current);
    if (!startPos || !targetPos) return;
    soundRef.current.unlock();
    setFlyingCard({
      card: state.discardPile,
      startPos,
      targetPos,
      sound: 'pickup',
      onComplete: () => handlePickUpFromPile(0),
    });
  }, [state.discardPile, handlePickUpFromPile, surgeReveal]);

  const getUserHandInsertIndexFromPoint = useCallback((clientX, clientY) => {
    const root = userHandMainRef.current;
    const fan = root?.querySelector('.hand-fan');
    if (!fan) return null;
    const rect = fan.getBoundingClientRect();
    const padX = Math.max(24, rect.width * 0.08);
    const padY = Math.max(26, rect.height * 0.55);
    const inside =
      clientX >= rect.left - padX &&
      clientX <= rect.right + padX &&
      clientY >= rect.top - padY &&
      clientY <= rect.bottom + padY;
    if (!inside) return null;
    const cardEls = Array.from(fan.querySelectorAll('[data-hand-card-id]'));
    if (cardEls.length === 0) return 0;
    const centers = cardEls
      .map((el) => {
        const r = el.getBoundingClientRect();
        return r.left + r.width / 2;
      })
      .sort((a, b) => a - b);
    if (clientX <= centers[0]) return 0;
    for (let i = 0; i < centers.length - 1; i += 1) {
      const mid = (centers[i] + centers[i + 1]) / 2;
      if (clientX < mid) return i + 1;
    }
    return centers.length;
  }, []);

  const handlePickUpToInsertIndex = useCallback((insertIndex) => {
    if (dealAnimatingRef.current) return;
    if (surgeReveal) return;
    if (!state.discardPile) return;
    const totalCardsByPlayer = state.hands.map((hand, i) => {
      const handCount = hand?.length ?? 0;
      const pileCount = (state.handPiles?.[i] || []).reduce(
        (sum, pile) => sum + (Array.isArray(pile) ? pile.length : 0),
        0
      );
      return handCount + pileCount;
    });
    const userTotalCardsNow = totalCardsByPlayer[0] ?? 0;
    const cardOnTableNow = state.discardPile !== null;
    const hasPickedUpNow = state.lastPickedUpCardId !== null;
    const someoneHasElevenNow = totalCardsByPlayer.some((n) => n === 11);
    const someoneHasNineNow = totalCardsByPlayer.some((n) => n === 9);
    const canPickUpNow =
      (
        state.phase === PHASES.PLAYING &&
        state.currentPlayerIndex === 0 &&
        cardOnTableNow &&
        !hasPickedUpNow
      ) ||
      (
        state.phase === PHASES.SURGE_GRACE &&
        cardOnTableNow &&
        !someoneHasElevenNow &&
        (userTotalCardsNow === 9 || (!someoneHasNineNow && userTotalCardsNow === 10))
      );
    if (!canPickUpNow) return;

    if (mpRef.current && gameMode === 'online') {
      pendingPickupInsertIndexRef.current = Number.isFinite(insertIndex) ? insertIndex : null;
      handlePickUpFromPile(0);
      return;
    }
    setState((s) => {
      const picked = s.discardPile;
      const next = pickUpFromPile(s, 0);
      if (next === s || !picked) return s;
      if (!Number.isFinite(insertIndex)) return next;
      const hand = next.hands?.[0] || [];
      const from = hand.findIndex((c) => c.id === picked.id);
      if (from < 0) return next;
      const to = Math.max(0, Math.min(insertIndex, hand.length - 1));
      if (from === to) return next;
      return reorderHand(next, 0, from, to);
    });
  }, [gameMode, handlePickUpFromPile, state, surgeReveal]);

  const handleDiscardPileClick = useCallback(() => {
    if (Date.now() < ignoreDiscardClickUntilRef.current) return;
    handlePickUpClick();
  }, [handlePickUpClick]);

  const handleDiscardPilePointerDown = useCallback((e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (dealAnimatingRef.current) return;
    if (surgeReveal) return;
    if (!state.discardPile) return;
    const pointerId = e.pointerId;
    const startX = e.clientX;
    const startY = e.clientY;
    let didDrag = false;
    const cleanup = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
    };
    const onPointerMove = (ev) => {
      if (ev.pointerId !== pointerId) return;
      if (!didDrag) {
        if (Math.hypot(ev.clientX - startX, ev.clientY - startY) < PICKUP_DRAG_THRESHOLD_PX) return;
        didDrag = true;
        ignoreDiscardClickUntilRef.current = Date.now() + 260;
      }
      if (didDrag && ev.pointerType !== 'mouse') ev.preventDefault();
    };
    const onPointerUp = (ev) => {
      if (ev.pointerId !== pointerId) return;
      if (didDrag) {
        const insertIndex = getUserHandInsertIndexFromPoint(ev.clientX, ev.clientY);
        if (insertIndex != null) {
          handlePickUpToInsertIndex(insertIndex);
        }
      }
      cleanup();
    };
    const onPointerCancel = (ev) => {
      if (ev.pointerId !== pointerId) return;
      cleanup();
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
  }, [getUserHandInsertIndexFromPoint, handlePickUpToInsertIndex, state.discardPile, surgeReveal]);

  const isBotTurn =
    gameMode === 'bots' &&
    state.phase === PHASES.PLAYING &&
    state.currentPlayerIndex !== 0 &&
    !dealAnimation &&
    !surgeReveal;
  const botTurnRef = useRef(false);

  const applyBotMoveInstantly = useCallback((snapshot, playerIndex) => {
    const move = getBotMove(snapshot);
    if (!move) return snapshot;
    if (move.action === 'serg') return callSerg(snapshot);
    if (move.action === 'discard' && move.card) return discardCard(snapshot, playerIndex, move.card);
    if (move.action === 'pickUpAndDiscard' && move.card) return pickUpAndDiscard(snapshot, playerIndex, move.card);
    return snapshot;
  }, []);

  useEffect(() => {
    if (!isBotTurn) {
      botTurnRef.current = false;
      botTurnDeadlineRef.current = { key: null, at: 0 };
      return undefined;
    }
    botTurnRef.current = true;
    const playerIndex = state.currentPlayerIndex;
    const turnKey = `${state.gameId || 'local'}:${state.roundNumber || 0}:${state.turnCount || 0}:${playerIndex}`;
    if (botTurnDeadlineRef.current.key !== turnKey) {
      const delay = getBotDelay(state.botDifficulty || 'medium') || BOT_DELAY_FALLBACK_MS;
      botTurnDeadlineRef.current = { key: turnKey, at: Date.now() + delay };
    }
    const runBotTurn = () => {
      const overdueMs = Date.now() - botTurnDeadlineRef.current.at;
      if (document.hidden || overdueMs > 650) {
        setState((s) => {
          if (
            s.phase !== PHASES.PLAYING ||
            s.currentPlayerIndex !== playerIndex ||
            s.currentPlayerIndex === 0
          ) {
            return s;
          }
          return applyBotMoveInstantly(s, playerIndex);
        });
        return;
      }
      if (Math.random() < BOT_BOND_QUOTE_CHANCE) {
        const nextQuote = pickBondQuote(lastQuoteByPlayerRef.current[playerIndex]);
        lastQuoteByPlayerRef.current[playerIndex] = nextQuote;
        showPlayerQuoteBubble(playerIndex, nextQuote);
      }
      const move = getBotMove(state);
      if (!move || !botTurnRef.current) return;
      if (move.action === 'serg') {
        handleSerg();
      } else if (move.action === 'discard' && move.card) {
        const seatPos = getCenter(getSeatPileEl(playerIndex));
        const discardPos = getCenter(discardTargetRef.current);
        if (!seatPos || !discardPos) return;
        if (state.discardPile) {
          // Opponents first collect the center card, then place their own card.
          setFlyingCard({
            card: state.discardPile,
            startPos: discardPos,
            targetPos: seatPos,
            sound: 'pickup',
            onComplete: () => {
              handlePickUpFromPile(playerIndex);
              setTimeout(() => {
                const discardStartPos = getCenter(getSeatPileEl(playerIndex));
                const discardTargetPos = getCenter(discardTargetRef.current);
                if (!discardStartPos || !discardTargetPos) return;
                setFlyingCard({
                  card: move.card,
                  startPos: discardStartPos,
                  targetPos: discardTargetPos,
                  sound: 'discard',
                  onComplete: (c) => {
                    if (c) handleDiscard(playerIndex, c);
                  },
                });
              }, 140);
            },
          });
        } else {
          setFlyingCard({
            card: move.card,
            startPos: seatPos,
            targetPos: discardPos,
            sound: 'discard',
            onComplete: (c) => {
              if (c) handleDiscard(playerIndex, c);
            },
          });
        }
      } else if (move.action === 'pickUpAndDiscard' && move.card && state.discardPile) {
        const startPos = getCenter(discardTargetRef.current);
        const targetPos = getCenter(getSeatPileEl(playerIndex));
        if (!startPos || !targetPos) return;
        setFlyingCard({
          card: state.discardPile,
          startPos,
          targetPos,
          sound: 'pickup',
          onComplete: () => {
            handlePickUpFromPile(playerIndex);
            setTimeout(() => {
              const discardStartPos = getCenter(getSeatPileEl(playerIndex));
              const discardTargetPos = getCenter(discardTargetRef.current);
              if (!discardStartPos || !discardTargetPos) return;
              setFlyingCard({
                card: move.card,
                startPos: discardStartPos,
                targetPos: discardTargetPos,
                sound: 'discard',
                onComplete: (c) => {
                  if (c) handleDiscard(playerIndex, c);
                },
              });
            }, 140);
          },
        });
      }
    };
    const remaining = botTurnDeadlineRef.current.at - Date.now();
    if (remaining <= 0) {
      runBotTurn();
      return undefined;
    }
    const timer = setTimeout(runBotTurn, remaining);
    return () => clearTimeout(timer);
  }, [
    isBotTurn,
    state,
    dealAnimation,
    handleSerg,
    handleDiscard,
    handlePickUpFromPile,
    getSeatPileEl,
    showPlayerQuoteBubble,
    applyBotMoveInstantly,
    backgroundActivityTick,
  ]);

  const surgeGraceBotHandSizeKey = useMemo(
    () => state.hands.map((h) => (Array.isArray(h) ? h.length : 0)).join(','),
    [state.hands]
  );
  useEffect(() => {
    if (state.phase !== PHASES.SURGE_GRACE || gameMode !== 'bots' || surgeReveal || dealAnimation) {
      surgeGraceBotDeadlineRef.current = { key: null, at: 0 };
      return undefined;
    }
    const idx = state.hands.findIndex((h, i) => i > 0 && h && h.length === 11);
    if (idx < 0) return undefined;
    const key = `${state.gameId || 'local'}:${state.roundNumber || 0}:${idx}:${state.lastPickedUpCardId || ''}:${surgeGraceBotHandSizeKey}`;
    if (surgeGraceBotDeadlineRef.current.key !== key) {
      const graceDelay = getBotDelay(state.botDifficulty || 'medium') || BOT_DELAY_FALLBACK_MS;
      surgeGraceBotDeadlineRef.current = { key, at: Date.now() + graceDelay };
    }
    const runEqualize = () => {
      setState((s) => {
        const h = s.hands[idx];
        if (!h || h.length !== 11) return s;
        const c = h.find((x) => x.id !== s.lastPickedUpCardId) ?? h[0];
        if (!c) return s;
        return surgeGraceEqualizeDiscard(s, idx, c);
      });
    };
    const remaining = surgeGraceBotDeadlineRef.current.at - Date.now();
    if (remaining <= 0) {
      runEqualize();
      return undefined;
    }
    const timer = setTimeout(runEqualize, remaining);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- surgeGraceBotHandSizeKey tracks hand counts; listing state.hands would reset the bot timer every render
  }, [
    state.phase,
    surgeGraceBotHandSizeKey,
    state.lastPickedUpCardId,
    gameMode,
    surgeReveal,
    dealAnimation,
    state.botDifficulty,
    state.gameId,
    state.roundNumber,
    backgroundActivityTick,
  ]);

  const userPiles = useMemo(() => (state.handPiles && state.handPiles[0]) || [], [state.handPiles]);
  const pileDropInteractionEnabled =
    gameMode !== 'online' &&
    (state.phase === PHASES.PLAYING || state.phase === PHASES.SURGE_GRACE) &&
    !surgeReveal &&
    !dealDealtCounts &&
    userPiles.length > 0;

  const getPileDropIndex = useCallback(
    (x, y) => {
      if (!pileDropInteractionEnabled) return null;
      if (dealAnimatingRef.current) return null;
      const refs = userPileDropRefs.current;
      for (let i = 0; i < userPiles.length; i += 1) {
        const el = refs[i];
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return i;
      }
      return null;
    },
    [pileDropInteractionEnabled, userPiles.length]
  );

  const isDiscardDropTarget = useCallback((x, y) => {
    if (dealAnimatingRef.current) return false;
    if (state.discardPile) return false;
    const el = discardTargetRef.current;
    if (!el) return false;
    const r = el.getBoundingClientRect();
    const padX = Math.max(20, r.width * 0.45);
    const padY = Math.max(20, r.height * 0.45);
    return (
      x >= r.left - padX &&
      x <= r.right + padX &&
      y >= r.top - padY &&
      y <= r.bottom + padY
    );
  }, [state.discardPile]);

  const handleDropOnPile = useCallback(
    (card, pileIndex) => {
      if (!pileDropInteractionEnabled) return;
      if (dealAnimatingRef.current) return;
      // Mouseup after drag can trigger pile onClick; ignore that immediate click.
      ignorePileClickUntilRef.current = Date.now() + 280;
      setHandSelectedIds((prev) => prev.filter((id) => id !== card.id));
      setState((s) => {
        const piles = (s.handPiles && s.handPiles[0]) || [];
        if (!piles[pileIndex]) return s;
        return addCardsToPile(s, 0, pileIndex, [card.id]);
      });
    },
    [pileDropInteractionEnabled]
  );

  useEffect(() => {
    if (!pileDropInteractionEnabled) setDragOverUserPileIndex(null);
  }, [pileDropInteractionEnabled]);

  const showChatPanel = state.phase !== PHASES.LOBBY && isOnline;

  const renderRulesContent = () => (
    <>
      <p><strong>Goal</strong>: First to 40 points wins.</p>
      <p><strong>Turn action</strong>: Do exactly one:</p>
      <ul className="game-screen__rules-list">
        <li>Put 1 card from your hand onto the centre pile.</li>
        <li>Pick up the centre card, then put down a different card.</li>
      </ul>
      <p><strong>Important</strong>: You cannot put down the card you just picked up.</p>
      <p><strong>Piles</strong>: Build scoring groups in your own area.</p>
      <ul className="game-screen__rules-list">
        <li>Use <strong>Make pile</strong> with 2+ selected cards, or drag onto an existing pile.</li>
        <li>You can break piles before SURGE is called.</li>
      </ul>
      <p><strong>SURGE</strong>: Calling SURGE starts grace period, then scoring.</p>
      <ul className="game-screen__rules-list">
        <li>Each player must finish with exactly 10 cards.</li>
        <li>If someone has 11 and someone has 9, one card is transferred from 11 to 9.</li>
        <li>You still cannot put down a just-picked-up card.</li>
      </ul>
      <p><strong>Scoring</strong>:</p>
      <ul className="game-screen__rules-list">
        <li>Valid pile groups score points.</li>
        <li>Each leftover hand card is -1 point.</li>
        <li>If all 10 cards are used in valid groups, you get +1 bonus.</li>
      </ul>
      <p><strong>Instant win</strong>: A 10-card straight flush in one suit wins the game immediately.</p>
    </>
  );

  if (state.phase === PHASES.LOBBY) {
    return (
      <div className="game-screen" style={gameScreenStyle}>
        <div className="game-screen__border-top">
          <Link to="/" className="game-screen__border-top-title">
            <img src={surgeName} alt="SURGE" className="game-screen__border-top-logo" />
          </Link>
          <div className="game-screen__border-top-actions">
            <button
              type="button"
              className="game-screen__border-top-btn"
              onClick={handleLobbyClick}
            >
              Lobby
            </button>
            <button
              type="button"
              className="game-screen__border-top-btn"
              onClick={() => setShowRulesOverlay(true)}
            >
              Rules
            </button>
            <button
              type="button"
              className="game-screen__border-top-btn"
              onClick={() => setShowPointsOverlay(true)}
            >
              Points & hands
            </button>
          </div>
        </div>
        <div className="game-screen__border-body">
          <div className="game-screen__gold-rim">
            <div className="game-screen__table-wrap game-screen__table-wrap--lobby">
              <Grainient
                className="game-screen__table-grainient"
                color1={themeConfig.table}
                color2={themeConfig.table}
                color3={themeConfig.table}
                timeSpeed={0}
                grainAnimated={false}
              />
              <div className="game-screen__lobby">
                {lobbyStep === 'choose' ? (
                  <>
                    <h2 className="game-screen__lobby-title">How do you want to play?</h2>
                    <div className="game-screen__lobby-mode-pick">
                      <button
                        type="button"
                        className="game-screen__mode-btn"
                        onClick={() => { setGameMode('bots'); setLobbyStep('setup'); }}
                      >
                        <span className="game-screen__mode-btn-icon">🤖</span>
                        <span className="game-screen__mode-btn-label">Play vs Bots</span>
                        <span className="game-screen__mode-btn-desc">Practice against AI opponents</span>
                      </button>
                      <button
                        type="button"
                        className="game-screen__mode-btn"
                        onClick={() => { setGameMode('online'); setLobbyStep('setup'); }}
                      >
                        <span className="game-screen__mode-btn-icon">🌐</span>
                        <span className="game-screen__mode-btn-label">Play Online</span>
                        <span className="game-screen__mode-btn-desc">Create or join a room with friends</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                <div className="game-screen__lobby-header">
                  <div className="game-screen__lobby-header-side game-screen__lobby-header-side--left">
                    <button
                      type="button"
                      className="btn-53 game-screen__start-game-float game-screen__lobby-header-btn game-screen__lobby-back"
                      onClick={() => { setLobbyStep('choose'); handleLeaveRoom(); }}
                      aria-label="Back"
                    >
                      <div className="original" aria-hidden>←</div>
                      <div className="letters" aria-hidden>
                        {['B', 'A', 'C', 'K'].map((ch, i) => (
                          <span key={i}>{ch}</span>
                        ))}
                      </div>
                    </button>
                  </div>
                  <h2 className="game-screen__lobby-title">{gameMode === 'bots' ? 'Play vs Bots' : 'Play Online'}</h2>
                  <div className="game-screen__lobby-header-side game-screen__lobby-header-side--right">
                    {gameMode === 'bots' && (
                      <button
                        type="button"
                        className="btn-53 game-screen__start-game-float game-screen__lobby-header-btn"
                        onClick={() => handleOpenQuickStart('bots')}
                        aria-label="Start game"
                      >
                        <div className="original">Start game</div>
                        <div className="letters" aria-hidden>
                          {['S', 'T', 'A', 'R', 'T'].map((ch, i) => (
                            <span key={i}>{ch}</span>
                          ))}
                        </div>
                      </button>
                    )}
                    {gameMode === 'online' && onlineLobby?.yourIndex === 0 && onlineLobby.players.length >= 2 && (
                      <button
                        type="button"
                        className="btn-53 game-screen__start-game-float game-screen__lobby-header-btn"
                        onClick={() => handleOpenQuickStart('online')}
                        aria-label="Start game"
                      >
                        <div className="original">Start game</div>
                        <div className="letters" aria-hidden>
                          {['S', 'T', 'A', 'R', 'T'].map((ch, i) => (
                            <span key={i}>{ch}</span>
                          ))}
                        </div>
                      </button>
                    )}
                  </div>
                </div>

                {gameMode === 'bots' ? (
                <div className="game-screen__lobby-group">
                  <div className="game-screen__lobby-group-title">Game setup</div>
                  <div className="game-screen__lobby-grid">
                    <div className="game-screen__lobby-section">
                      <div className="game-screen__lobby-section-title">Number of players</div>
                      <div className="game-screen__player-options">
                        {[2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            type="button"
                            className={`game-screen__player-opt ${playerCount === n ? 'game-screen__player-opt--selected' : ''}`}
                            onClick={() => setPlayerCount(n)}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="game-screen__lobby-section">
                      <div className="game-screen__lobby-section-title">Bot difficulty</div>
                      <div className="game-screen__player-options">
                        {['easy', 'medium', 'hard'].map((level) => (
                          <button
                            key={level}
                            type="button"
                            className={`game-screen__player-opt ${settings.botDifficulty === level ? 'game-screen__player-opt--selected' : ''}`}
                            onClick={() => setSettings((prev) => ({ ...prev, botDifficulty: level }))}
                          >
                            {level.charAt(0).toUpperCase() + level.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="game-screen__lobby-section game-screen__lobby-section--full">
                      <div className="game-screen__lobby-section-title">Player names</div>
                      <div className="game-screen__player-names">
                        {Array.from({ length: playerCount }).map((_, idx) => (
                          <label key={`player-name-${idx}`} className="game-screen__player-name">
                            <span className="game-screen__player-name-label">
                              {idx === 0 ? 'You' : `P${idx + 1}`}
                            </span>
                            <input
                              type="text"
                              className="game-screen__player-name-input"
                              value={playerNames[idx] ?? ''}
                              maxLength={16}
                              placeholder={`Player ${idx + 1}`}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPlayerNames((prev) => {
                                  const next = [...prev];
                                  next[idx] = value;
                                  return next;
                                });
                              }}
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                ) : (
                <div className="game-screen__lobby-group">
                  <div className="game-screen__lobby-group-title">Online game</div>
                  <div className="game-screen__lobby-grid">
                    <div className="game-screen__lobby-section">
                      <div className="game-screen__lobby-section-title">Your name</div>
                      <input
                        type="text"
                        className="game-screen__player-name-input"
                        value={playerNames[0] ?? ''}
                        maxLength={16}
                        placeholder="Your name"
                        onChange={(e) => {
                          const value = e.target.value;
                          setPlayerNames((prev) => { const next = [...prev]; next[0] = value; return next; });
                        }}
                      />
                    </div>
                    {!onlineLobby ? (
                      <div className="game-screen__lobby-section game-screen__lobby-section--full">
                        <div className="game-screen__online-lobby-pick">
                          <div className="game-screen__online-panel">
                            <div className="game-screen__online-panel-title">Create a room</div>
                            <p className="game-screen__online-panel-desc">You are the host. Share the code with everyone else after the room is created.</p>
                            <button type="button" className="game-screen__btn game-screen__btn--primary game-screen__online-panel-btn" onClick={handleCreateRoom}>
                              Create room
                            </button>
                          </div>
                          <div className="game-screen__online-lobby-divider" aria-hidden="true" />
                          <div className="game-screen__online-panel game-screen__online-panel--join">
                            <div className="game-screen__online-panel-title">Join a room</div>
                            <div className="game-screen__online-panel-desc game-screen__online-panel-desc--with-code">
                              <span className="game-screen__online-panel-desc-text">
                                Enter the code your host sent you, then join.
                              </span>
                              <input
                                id="surge-room-code-input"
                                type="text"
                                className="game-screen__player-name-input game-screen__room-code-input"
                                value={joinCode}
                                maxLength={6}
                                placeholder="e.g. ABCD12"
                                autoComplete="off"
                                spellCheck={false}
                                aria-label="Room code"
                                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                                style={{ textTransform: 'uppercase', letterSpacing: '2px', textAlign: 'center' }}
                              />
                            </div>
                            <button type="button" className="game-screen__btn game-screen__btn--primary game-screen__online-panel-btn" onClick={handleJoinRoom} disabled={joinCode.length < 4}>
                              Join room
                            </button>
                          </div>
                        </div>
                        {onlineError && <div className="game-screen__online-error">{onlineError}</div>}
                      </div>
                    ) : (
                      <div className="game-screen__lobby-section game-screen__lobby-section--full">
                        <div className="game-screen__online-room">
                          {onlineLobby.yourIndex === 0 && (
                            <div className="game-screen__online-turn-limit">
                              <span className="game-screen__online-turn-limit-label">Turn timer</span>
                              <div className="game-screen__player-options">
                                {[0, 15, 30, 45, 60].map((sec) => (
                                  <button
                                    key={sec}
                                    type="button"
                                    className={`game-screen__player-opt ${onlineTurnLimitSec === sec ? 'game-screen__player-opt--selected' : ''}`}
                                    onClick={() => setOnlineTurnLimitSec(sec)}
                                  >
                                    {sec === 0 ? 'Off' : `${sec}s`}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="game-screen__online-code">
                            Room: <strong>{roomCode}</strong>
                          </div>
                          <div className="game-screen__online-players">
                            {onlineLobby.players.map((p, i) => (
                              <div key={i} className={`game-screen__online-player ${p.connected ? '' : 'game-screen__online-player--disconnected'}`}>
                                {p.name} {i === 0 && '(host)'} {!p.connected && '(disconnected)'}
                              </div>
                            ))}
                          </div>
                          {onlineLobby.players.length < 2 && (
                            <div className="game-screen__online-waiting">Waiting for players to join...</div>
                          )}
                          <div className="game-screen__online-actions">
                            <button type="button" className="game-screen__btn" onClick={handleLeaveRoom}>
                              Leave room
                            </button>
                          </div>
                          {onlineError && <div className="game-screen__online-error">{onlineError}</div>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                )}

                <div className="game-screen__lobby-group">
                  <div className="game-screen__lobby-group-title">Preferences</div>
                  <div className="game-screen__lobby-grid">
                    <div className="game-screen__lobby-section">
                      <div className="game-screen__lobby-section-title">Sound</div>
                      <div className="game-screen__lobby-toggle-row">
                        <label className="game-screen__toggle">
                          <input
                            type="checkbox"
                            checked={!settings.soundMuted}
                            onChange={(e) =>
                              setSettings((prev) => ({
                                ...prev,
                                soundMuted: !e.target.checked,
                                ambienceEnabled: e.target.checked ? prev.ambienceEnabled : false,
                              }))
                            }
                          />
                          Sound
                        </label>
                        <label className="game-screen__toggle">
                          <input
                            type="checkbox"
                            checked={settings.ambienceEnabled}
                            disabled={settings.soundMuted}
                            onChange={(e) =>
                              setSettings((prev) => ({
                                ...prev,
                                ambienceEnabled: e.target.checked,
                              }))
                            }
                          />
                          Ambience
                        </label>
                      </div>
                    </div>
                    <div className="game-screen__lobby-section">
                      <div className="game-screen__lobby-section-title">Theme</div>
                      <div className="game-screen__player-options">
                        {Object.entries(THEMES).map(([key, value]) => (
                          <button
                            key={key}
                            type="button"
                            className={`game-screen__player-opt ${settings.theme === key ? 'game-screen__player-opt--selected' : ''}`}
                            onClick={() => setSettings((prev) => ({ ...prev, theme: key }))}
                          >
                            {value.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="game-screen__lobby-group">
                  <div className="game-screen__lobby-group-title">Profile</div>
                  <div className="game-screen__lobby-grid">
                    <div className="game-screen__lobby-section game-screen__lobby-section--full">
                      <div className="game-screen__stats-grid">
                        <div className="game-screen__stat">
                          <span className="game-screen__stat-label">Games</span>
                          <span className="game-screen__stat-value">{stats.gamesPlayed}</span>
                        </div>
                        <div className="game-screen__stat">
                          <span className="game-screen__stat-label">Wins</span>
                          <span className="game-screen__stat-value">{stats.wins}</span>
                        </div>
                        <div className="game-screen__stat">
                          <span className="game-screen__stat-label">Best score</span>
                          <span className="game-screen__stat-value">{stats.highScore}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {showQuickStartOverlay && (
          <div
            className="game-screen__modal-overlay game-screen__modal-overlay--quickstart"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quick-start-title"
          >
            <div className="game-screen__modal" onClick={(e) => e.stopPropagation()}>
              <div className="game-screen__modal-head">
                <h3 id="quick-start-title">How to Play</h3>
              </div>
              <div className="game-screen__modal-body">
                <div className="game-screen__quick-start-modal-list" role="list">
                  {QUICK_START_STEPS.map((step) => (
                    <div key={step.keyword} className="game-screen__quick-start-modal-item" role="listitem">
                      <div className="game-screen__quick-start-modal-title">{step.keyword}</div>
                      <p className="game-screen__quick-start-modal-body">{step.body}</p>
                    </div>
                  ))}
                </div>
                <div className="game-screen__modal-actions">
                  <button
                    type="button"
                    className="game-screen__btn game-screen__btn--primary"
                    onClick={handleCloseQuickStart}
                  >
                    Understood
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showRulesOverlay && (
          <div
            className="game-screen__modal-overlay game-screen__modal-overlay--rules"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rules-title"
            onClick={() => setShowRulesOverlay(false)}
          >
            <div className="game-screen__modal" onClick={(e) => e.stopPropagation()}>
              <div className="game-screen__modal-head">
                <h3 id="rules-title">Rules</h3>
                <button
                  type="button"
                  className="game-screen__modal-close"
                  onClick={() => setShowRulesOverlay(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="game-screen__modal-body">{renderRulesContent()}</div>
            </div>
          </div>
        )}

        {showPointsOverlay && (
          <div
            className="game-screen__modal-overlay game-screen__modal-overlay--points"
            role="dialog"
            aria-modal="true"
            aria-labelledby="points-title"
            onClick={() => setShowPointsOverlay(false)}
          >
            <div className="game-screen__modal" onClick={(e) => e.stopPropagation()}>
              <div className="game-screen__modal-head">
                <h3 id="points-title">Points & hands</h3>
                <button
                  type="button"
                  className="game-screen__modal-close"
                  onClick={() => setShowPointsOverlay(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="game-screen__modal-body">
                <p>Your hand is scored by counting the best combination of groups.</p>
                <ul className="game-screen__points-list">
                  <li><span className="game-screen__points-list-text"><strong>Pair</strong> (2 of a kind): <strong>1 pt</strong></span><PointsCardFan cards={POINTS_HAND_EXAMPLES[0]} /></li>
                  <li><span className="game-screen__points-list-text"><strong>Triple</strong> (3 of a kind): <strong>2 pts</strong></span><PointsCardFan cards={POINTS_HAND_EXAMPLES[1]} /></li>
                  <li><span className="game-screen__points-list-text"><strong>Four of a kind</strong>: <strong>5 pts</strong> (four Aces: <strong>7 pts</strong>)</span><PointsCardFan cards={POINTS_HAND_EXAMPLES[2]} /></li>
                  <li><span className="game-screen__points-list-text"><strong>Full house</strong> (triple + pair): <strong>4 pts</strong></span><PointsCardFan cards={POINTS_HAND_EXAMPLES[3]} /></li>
                  <li><span className="game-screen__points-list-text"><strong>Straight</strong> (5+ cards in sequence): <strong>3 pts</strong> (10 cards: <strong>8 pts</strong>)</span><PointsCardFan cards={POINTS_HAND_EXAMPLES[4]} /></li>
                  <li><span className="game-screen__points-list-text"><strong>Flush</strong> (5+ same suit, not in sequence): <strong>3 pts</strong> (10 cards: <strong>8 pts</strong>)</span><PointsCardFan cards={POINTS_HAND_EXAMPLES[5]} /></li>
                  <li><span className="game-screen__points-list-text"><strong>Straight flush</strong> (same suit in sequence): <strong>3 pts</strong> + 1 per extra card (e.g. 5 cards = <strong>5 pts</strong>). Royal straight flush (10–A): <strong>10 pts</strong>.</span><PointsCardFan cards={POINTS_HAND_EXAMPLES[6]} /></li>
                </ul>
                <p>Unused cards cost 1 point each. If you use every card in your hand in scoring groups, you get a +1 bonus.</p>
                <p><strong>Winning the game:</strong> A 10-card straight flush in one suit wins the whole game. Otherwise first to 40 points wins.</p>
              </div>
            </div>
          </div>
        )}

        {showLobbyConfirm && (
          <div
            className="game-screen__modal-overlay"
            role="dialog"
            aria-modal="true"
            onClick={handleCancelLobby}
          >
            <div className="game-screen__modal" onClick={(e) => e.stopPropagation()}>
              <h3>Return to lobby?</h3>
              <p>Change players? Current game will end.</p>
              <div className="game-screen__modal-actions">
                <button type="button" className="game-screen__btn game-screen__btn--secondary" onClick={handleCancelLobby}>
                  Cancel
                </button>
                <button type="button" className="game-screen__btn game-screen__btn--primary" onClick={handleConfirmLobby}>
                  Go to lobby
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const showRoundEnd = state.phase === PHASES.ROUND_END; // eslint-disable-line no-unused-vars
  const showGameOver = state.phase === PHASES.GAME_OVER;
  const userHand = state.hands[0] || [];
  const totalCardsByPlayer = state.hands.map((hand, i) => {
    const handCount = hand?.length ?? 0;
    const pileCount = (state.handPiles?.[i] || []).reduce(
      (sum, pile) => sum + (Array.isArray(pile) ? pile.length : 0),
      0
    );
    return handCount + pileCount;
  });
  const userTotalCards = totalCardsByPlayer[0] ?? 0;
  const cardOnTable = state.discardPile !== null;
  const hasPickedUp = state.lastPickedUpCardId !== null;
  const isUserTurn = state.currentPlayerIndex === 0;
  const someoneHasEleven = totalCardsByPlayer.some((n) => n === 11);
  const someoneHasNine = totalCardsByPlayer.some((n) => n === 9);
  const canPickUpInSurgeGrace =
    state.phase === PHASES.SURGE_GRACE &&
    !dealAnimation &&
    cardOnTable &&
    !someoneHasEleven &&
    (userTotalCards === 9 || (!someoneHasNine && userTotalCards === 10));
  const canPickUpInPlaying =
    state.phase === PHASES.PLAYING &&
    !dealAnimation &&
    state.currentPlayerIndex === 0 &&
    cardOnTable &&
    !hasPickedUp;

  const surgePileClass = (pileIdx) => {
    if (!surgeReveal) return '';
    const { phases, phaseIndex } = surgeReveal;
    const step = phases.findIndex((p) => p.type === 'pile' && p.index === pileIdx);
    if (step < 0) return '';
    if (phaseIndex > step) return 'game-screen__pile--surge-done';
    if (phaseIndex === step) return 'game-screen__pile--surge-active';
    return '';
  };
  const surgePhaseNow = surgeReveal?.phases[surgeReveal.phaseIndex];
  const surgePendingDetail = surgeReveal?.pending?.roundDetails?.[0];
  const showSurgePenalty = Boolean(surgeReveal && surgePhaseNow?.type === 'penalty');
  const showSurgeBonus = Boolean(
    surgeReveal &&
    surgePhaseNow?.type === 'bonus' &&
    (surgePendingDetail?.roundShellBonus ?? 0) > 0
  );
  const showSurgeMaxPotential = Boolean(
    surgeReveal &&
    surgePhaseNow?.type === 'max' &&
    Number.isFinite(surgePendingDetail?.maxPotential)
  );
  const getSpecialPileHand = (pileScore) => {
    if (!pileScore || pileScore.points <= 0) return null;
    const key = pileScore.type;
    if (!SPECIAL_HAND_LABELS[key]) return null;
    return {
      label: SPECIAL_HAND_LABELS[key],
      key,
    };
  };
  const scoreFocusPlayerIndex =
    surgePhaseNow?.type === 'fly'
      ? 0
      : surgePhaseNow?.type === 'otherTotal'
        ? surgePhaseNow.index
        : null;
  const revealActiveOpponentIndex =
    surgePhaseNow?.type === 'otherReveal' ||
    surgePhaseNow?.type === 'otherRevealPile' ||
    surgePhaseNow?.type === 'otherTotal'
      ? surgePhaseNow.index
      : null;
  const revealVisiblePileCounts = (() => {
    if (!surgeReveal) return {};
    const out = {};
    surgeReveal.phases.forEach((phase, idx) => {
      if (idx > surgeReveal.phaseIndex) return;
      if (!Number.isFinite(phase?.index)) return;
      const playerIndex = phase.index;
      if (phase.type === 'otherReveal') {
        out[playerIndex] = Math.max(out[playerIndex] || 0, 0);
      } else if (phase.type === 'otherRevealPile') {
        out[playerIndex] = Math.max(out[playerIndex] || 0, (phase.pileIndex ?? -1) + 1);
      } else if (phase.type === 'otherTotal') {
        const allPiles = surgeReveal.pending?.roundDetails?.[playerIndex]?.pileScores || [];
        const revealable = allPiles.filter((p) => Array.isArray(p?.cards) && p.cards.length > 0).length;
        out[playerIndex] = Math.max(out[playerIndex] || 0, revealable);
      }
    });
    return out;
  })();
  const revealedOpponentIndices =
    Number.isFinite(revealActiveOpponentIndex) ? [revealActiveOpponentIndex] : [];
  const displayGameScores = (() => {
    const scores =
      surgeReveal?.pending?.gameScores && surgeReveal?.pending?.roundScores
        ? surgeReveal.pending.gameScores.map(
            (score, i) => score - (surgeReveal.pending.roundScores?.[i] ?? 0)
          )
        : [...(state.gameScores || [])];
    if (surgeFlyScore != null) scores[0] = surgeFlyScore;
    if (surgeOtherFlyScore && Number.isFinite(surgeOtherFlyScore.index)) {
      scores[surgeOtherFlyScore.index] = surgeOtherFlyScore.value;
    }
    return scores;
  })();
  const roundDeltaByPlayer = surgeReveal?.pending?.roundScores || state.roundScores || [];

  const handleCardSelect = (card) => {
    setTooltip(null);
    if (dealAnimatingRef.current) return;
    if (surgeReveal) return;
    if (state.phase !== PHASES.PLAYING && state.phase !== PHASES.SURGE_GRACE) return;
    setHandSelectedIds((prev) => {
      const next = prev.includes(card.id) ? prev.filter((id) => id !== card.id) : [...prev, card.id];
      if (next.length === 1) {
        showHintOnce('discard', "Select another card to make a pile, or tap Put card down / drag to discard one.", 4500);
      } else if (next.length === 2) {
        showHintOnce('pile', "Tap Make pile to confirm, or tap a card to deselect.", 4000);
      }
      return next;
    });
  };

  const handleConfirmDiscard = () => {
    if (dealAnimatingRef.current) return;
    if (flyingCard) return;
    const surgeEqualize =
      state.phase === PHASES.SURGE_GRACE && userTotalCards === 11;
    if (state.phase !== PHASES.PLAYING && !surgeEqualize) return;
    if (state.phase === PHASES.PLAYING && !isUserTurn) return;
    if (state.discardPile) {
      showTooltip('Pick up the center card first.', 2200, 'fan');
      return;
    }
    if (handSelectedIds.length !== 1) return;
    const card = userHand.find((c) => c.id === handSelectedIds[0]);
    if (!card) return;
    if (card.id === state.lastPickedUpCardId) {
      showTooltip(CANNOT_DISCARD_PICKED_UP_MSG, 2600, 'fan');
      return;
    }
    const startPos = getHandCardCenter(card.id) ?? getCenter(handTargetRef.current);
    const targetPos = getCenter(discardTargetRef.current);
    if (!startPos || !targetPos) return;
    soundRef.current.unlock();
    setFlyingCard({
      card,
      startPos,
      targetPos,
      sound: 'discard',
      onComplete: (c) => {
        if (c) handleDiscard(0, c);
      },
    });
    setHandSelectedIds([]);
  };

  const handleDragDiscard = (card) => {
    if (dealAnimatingRef.current) return;
    if (flyingCard) return;
    const surgeEqualize =
      state.phase === PHASES.SURGE_GRACE && userTotalCards === 11;
    if (state.phase !== PHASES.PLAYING && !surgeEqualize) return;
    if (state.phase === PHASES.PLAYING && !isUserTurn) return;
    if (state.discardPile) {
      showTooltip('Pick up the center card first.', 2200, 'fan');
      return;
    }
    if (card.id === state.lastPickedUpCardId) {
      showTooltip(CANNOT_DISCARD_PICKED_UP_MSG, 2600, 'fan');
      return;
    }
    const startPos = getHandCardCenter(card.id) ?? getCenter(handTargetRef.current);
    const targetPos = getCenter(discardTargetRef.current);
    if (!startPos || !targetPos) return;
    soundRef.current.unlock();
    setFlyingCard({
      card,
      startPos,
      targetPos,
      sound: 'discard',
      onComplete: (c) => {
        if (c) handleDiscard(0, c);
      },
    });
    setHandSelectedIds([]);
  };

  const handleConfirmPile = () => {
    if (state.phase !== PHASES.PLAYING && state.phase !== PHASES.SURGE_GRACE) return;
    if (handSelectedIds.length < 2) return;
    if (mpRef.current && gameMode === 'online') {
      mpRef.current.createPile(handSelectedIds);
    } else {
      setState((s) => createPile(s, 0, handSelectedIds));
    }
    showHintOnce('privatePile', "Only you can see your piles. Other players can't see them.", 3600, 'piles');
    setHandSelectedIds([]);
  };

  const handleBreakPile = (pileIndex) => {
    if (state.phase !== PHASES.PLAYING && state.phase !== PHASES.SURGE_GRACE) return;
    if (mpRef.current && gameMode === 'online') {
      mpRef.current.breakPile(pileIndex);
    } else {
      setState((s) => breakPile(s, 0, pileIndex));
    }
  };

  const handlePileCardClick = (pileIndex, cardId) => {
    if (dealAnimatingRef.current || surgeReveal) return;
    if (state.phase !== PHASES.PLAYING && state.phase !== PHASES.SURGE_GRACE) return;
    if (cardId == null) return;
    setHandSelectedIds([]);
    if (mpRef.current && gameMode === 'online') {
      mpRef.current.returnPileCard?.(pileIndex, cardId);
    } else {
      setState((s) => returnCardFromPile(s, 0, pileIndex, cardId));
    }
  };

  const displayUserScore = displayGameScores[0] ?? 0;
  const surgeGraceRemainingSeconds = state.phase === PHASES.SURGE_GRACE && state.surgeGraceEndsAt
    ? Math.max(0, Math.ceil((state.surgeGraceEndsAt - surgeGraceNow) / 1000))
    : 0;
  const readyPlayers = Array.isArray(state.readyPlayers) ? state.readyPlayers : [];
  const readyNames = readyPlayers
    .map((isReady, i) => (isReady ? state.playerNames[i] : null))
    .filter(Boolean);
  const hideDiscardCard =
    !dealAnimation &&
    Boolean(flyingCard?.sound === 'pickup' && flyingCard?.startPos && flyingCard?.targetPos);
  const hiddenHandCardIds =
    flyingCard?.sound === 'discard' && flyingCard?.card?.id
      ? [flyingCard.card.id]
      : [];
  const chatPanelStyle =
    chatOpen && isDesktopReveal
      ? {
          ...(chatPosition ? { left: `${chatPosition.x}px`, top: `${chatPosition.y}px`, right: 'auto' } : {}),
          ...(chatSize ? { width: `${chatSize.width}px`, height: `${chatSize.height}px` } : {}),
        }
      : undefined;

  return (
      <div className={`game-screen${surgeReveal ? ' game-screen--surge-reveal' : ''}`} style={gameScreenStyle}>
      <div className="game-screen__border-top game-screen__border-top--in-game">
        <span
          className="game-screen__border-top-title"
          role="button"
          tabIndex={0}
          onClick={handleLobbyClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleLobbyClick();
            }
          }}
        >
          <img src={surgeName} alt="SURGE" className="game-screen__border-top-logo" />
        </span>
        {isDesktopReveal ? (
          <div className="game-screen__border-top-actions">
            <button
              type="button"
              className="game-screen__border-top-btn"
              onClick={handleLobbyClick}
            >
              Lobby
            </button>
            <button
              type="button"
              className="game-screen__border-top-btn"
              onClick={() => setShowRulesOverlay(true)}
            >
              Rules
            </button>
            <button
              type="button"
              className="game-screen__border-top-btn"
              onClick={() => setShowPointsOverlay(true)}
            >
              Points & hands
            </button>
          </div>
        ) : null}
        <div className="game-screen__border-top-right">
          {!isDesktopReveal && (
            <div
              ref={mobileHeaderMenuRef}
              className="game-screen__mobile-header-menu"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="game-screen__mobile-header-menu-btn"
                onClick={() => setMobileHeaderMenuOpen((open) => !open)}
                aria-expanded={mobileHeaderMenuOpen}
                aria-label="Open game menu"
              >
                <svg className="game-screen__mobile-header-menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <line x1="4" y1="7" x2="20" y2="7" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="17" x2="20" y2="17" />
                </svg>
              </button>
              {mobileHeaderMenuOpen && (
                <div className="game-screen__mobile-header-menu-panel" role="menu">
                  <button
                    type="button"
                    className="game-screen__mobile-header-menu-item"
                    onClick={() => {
                      setMobileHeaderMenuOpen(false);
                      handleLobbyClick();
                    }}
                  >
                    Lobby
                  </button>
                  <button
                    type="button"
                    className="game-screen__mobile-header-menu-item"
                    onClick={() => {
                      setMobileHeaderMenuOpen(false);
                      setShowRulesOverlay(true);
                    }}
                  >
                    Rules
                  </button>
                  <button
                    type="button"
                    className="game-screen__mobile-header-menu-item"
                    onClick={() => {
                      setMobileHeaderMenuOpen(false);
                      setShowPointsOverlay(true);
                    }}
                  >
                    Points & hands
                  </button>
                </div>
              )}
            </div>
          )}
          {showChatPanel && (
            <button
              type="button"
              className="game-screen__chat-icon-btn"
              onClick={handleToggleChat}
              aria-expanded={chatOpen}
              aria-label={chatOpen ? 'Close chat' : 'Open chat'}
              title={chatOpen ? 'Close chat' : 'Open chat'}
            >
              <svg className="game-screen__chat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
              </svg>
              {(!chatOpen && chatUnread > 0) && (
                <span className="game-screen__chat-unread">{chatUnread > 99 ? '99+' : chatUnread}</span>
              )}
            </button>
          )}
          <button
            type="button"
            className="game-screen__sound-toggle"
            onClick={() => setSettings((prev) => ({ ...prev, soundMuted: !prev.soundMuted }))}
            aria-label={settings.soundMuted ? 'Unmute sound' : 'Mute sound'}
            title={settings.soundMuted ? 'Unmute sound' : 'Mute sound'}
          >
            {settings.soundMuted ? (
              <svg className="game-screen__sound-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            ) : (
              <svg className="game-screen__sound-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
              </svg>
            )}
          </button>
        </div>
      </div>
      <div className="game-screen__border-body">
        <div className="game-screen__gold-rim">
          <div className="game-screen__table-wrap">
            <Grainient
              className="game-screen__table-grainient"
              color1={themeConfig.table}
              color2={themeConfig.table}
              color3={themeConfig.table}
              timeSpeed={0}
              grainAnimated={false}
            />
            <div className={`game-screen__play-area${dealAnimation ? ' game-screen__play-area--dealing' : ''}`}>
            <PokerTable
              hands={state.hands}
              discardPile={state.discardPile}
              showDealDeck={Boolean(dealAnimation)}
              hideDiscardCard={hideDiscardCard}
              currentPlayerIndex={state.currentPlayerIndex}
              numPlayers={state.numPlayers}
              playerNames={state.playerNames}
              gameScores={displayGameScores}
              scoreFocusPlayerIndex={scoreFocusPlayerIndex}
              roundDeltaByPlayer={roundDeltaByPlayer}
              opponentPiles={state.handPiles}
              opponentRoundDetails={surgeReveal?.pending?.roundDetails || state.roundDetails}
              revealedOpponentIndices={revealedOpponentIndices}
              revealVisiblePileCounts={revealVisiblePileCounts}
              revealActiveOpponentIndex={revealActiveOpponentIndex}
              roundLabel={`Round ${state.roundNumber ?? 1}`}
              discardTargetRef={discardTargetRef}
              dealBackCounts={dealDealtCounts}
              onDiscardPileClick={
                canPickUpInPlaying || canPickUpInSurgeGrace
                  ? handleDiscardPileClick
                  : undefined
              }
              onDiscardPilePointerDown={
                canPickUpInPlaying || canPickUpInSurgeGrace
                  ? handleDiscardPilePointerDown
                  : undefined
              }
              currentTurnSecondsRemaining={
                state.phase === PHASES.PLAYING && state.turnDeadlineAt
                  ? Math.max(0, Math.ceil((state.turnDeadlineAt - turnTimerNow) / 1000))
                  : null
              }
              quoteByPlayerIndex={playerQuoteBubbles}
              seatPileRefs={seatPileRefs}
            />
            {state.phase === PHASES.SURGE_GRACE && (
              <div className="game-screen__surge-grace-banner">
                <div className="game-screen__bot-turn">
                  SURGE called - finalize your piles ({surgeGraceRemainingSeconds}s)
                </div>
                {userTotalCards === 11 && (
                  <div className="game-screen__surge-equalize-hint" role="status">
                    {SURGE_EQUALIZE_HINT}
                  </div>
                )}
                <div className="game-screen__surge-ready-list">
                  Ready: {readyNames.length > 0 ? readyNames.join(', ') : 'No one yet'}
                </div>
              </div>
            )}
            {(state.phase === PHASES.PLAYING || state.phase === PHASES.SURGE_GRACE || Boolean(surgeReveal)) && (
              <div className="game-screen__hand-area">
                {userPiles.length > 0 && (
                  <div className="game-screen__piles-column">
                    {tooltip && tooltipAnchor === 'piles' && (
                      <div className="game-screen__tooltip game-screen__tooltip--piles" onClick={() => setTooltip(null)}>
                        <span className="game-screen__tooltip-text">{tooltip}</span>
                      </div>
                    )}
                    <div className="game-screen__piles">
                      {userPiles.map((pile, pi) => {
                        const sc = surgePileClass(pi);
                        const pileScore = surgePendingDetail?.pileScores?.[pi] || null;
                        const pilePts = pileScore?.points ?? 0;
                        const ptsValid = pilePts > 0;
                        const showSurgeScore = sc === 'game-screen__pile--surge-done' || sc === 'game-screen__pile--surge-active';
                        const specialHand = getSpecialPileHand(pileScore);
                        const pileStepPx = isDesktopReveal ? 11 : 9;
                        const fanSpread = pile.length <= 1 ? 0 : Math.min(isDesktopReveal ? 16 : 13, 4 + pile.length * 1.25);
                        const fanStart = -fanSpread / 2;
                        const fanStep = pile.length <= 1 ? 0 : fanSpread / Math.max(1, pile.length - 1);
                        const fanWidth = Math.max(84, 72 + Math.max(0, pile.length - 1) * pileStepPx + 20);
                        const centerOffset = (pile.length - 1) / 2;
                        return (
                          <div
                            key={pi}
                            ref={(el) => {
                              userPileDropRefs.current[pi] = el;
                            }}
                            className={`game-screen__pile ${sc}${
                              pileDropInteractionEnabled && dragOverUserPileIndex === pi
                                ? ' game-screen__pile--drag-hover'
                                : ''
                            }`}
                            onClick={() => {
                            if (Date.now() < ignorePileClickUntilRef.current) return;
                              if (!surgeReveal) handleBreakPile(pi);
                            }}
                            role="button"
                            tabIndex={surgeReveal ? -1 : 0}
                          >
                            <div className="game-screen__pile-surge-inner game-screen__pile-surge-inner--fan" style={{ width: `${fanWidth}px` }}>
                              {pile.map((c, ci) => (
                                <div
                                  key={c.id}
                                  className="game-screen__pile-card game-screen__pile-card--user"
                                  style={{
                                    '--pile-x': `${(ci - centerOffset) * pileStepPx}px`,
                                    '--pile-y': `${Math.abs(ci - centerOffset) * (isDesktopReveal ? 1.1 : 0.9)}px`,
                                    '--pile-rot': `${fanStart + fanStep * ci}deg`,
                                    '--pile-z': `${ci + 1}`,
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePileCardClick(pi, c.id);
                                  }}
                                  role="button"
                                  tabIndex={surgeReveal ? -1 : 0}
                                  onKeyDown={(e) => {
                                    if (e.key !== 'Enter' && e.key !== ' ') return;
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handlePileCardClick(pi, c.id);
                                  }}
                                >
                                  <div className={`card card--face-up ${c.suit === 'h' || c.suit === 'd' ? 'card--red' : ''} card--disabled`}>
                                    <span className="card__corner card__corner--top">
                                      <span className="card__rank">{{ 1: 'A', 11: 'J', 12: 'Q', 13: 'K' }[c.rank] || c.rank}</span>
                                      <span className="card__suit">{{ s: '♠', h: '♥', d: '♦', c: '♣' }[c.suit]}</span>
                                    </span>
                                    <span className="card__center">
                                      <span className="card__center-rank">{{ 1: 'A', 11: 'J', 12: 'Q', 13: 'K' }[c.rank] || c.rank}</span>
                                      <span className="card__center-suit">{{ s: '♠', h: '♥', d: '♦', c: '♣' }[c.suit]}</span>
                                    </span>
                                    <span className="card__corner card__corner--bottom">
                                      <span className="card__rank">{{ 1: 'A', 11: 'J', 12: 'Q', 13: 'K' }[c.rank] || c.rank}</span>
                                      <span className="card__suit">{{ s: '♠', h: '♥', d: '♦', c: '♣' }[c.suit]}</span>
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {showSurgeScore && (
                              <div
                                className={`game-screen__pile-surge-score ${ptsValid ? 'game-screen__pile-surge-score--ok' : 'game-screen__pile-surge-score--bad'}`}
                                aria-hidden
                              >
                                {pilePts > 0 ? `+${pilePts}` : `${pilePts}`}
                              </div>
                            )}
                            {showSurgeScore && specialHand && (
                              <div
                                className={`game-screen__pile-surge-hand game-screen__pile-surge-hand--${specialHand.key}`}
                                aria-hidden
                              >
                                {specialHand.label}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="game-screen__hand-block">
                  <Martini
                    className="game-screen__martini"
                    onActivate={handleMartiniActivate}
                    quoteText={playerQuoteBubbles[0] || null}
                  />
                  <div ref={userHandMainRef} className="game-screen__hand-main">
              <div className="game-screen__turn">
                {isUserTurn ? 'Your turn' : `${state.playerNames[state.currentPlayerIndex]}'s turn`}
                {state.phase === PHASES.PLAYING && state.turnDeadlineAt && (
                  <span className="game-screen__turn-timer">
                    {Math.max(0, Math.ceil((state.turnDeadlineAt - turnTimerNow) / 1000))}s
                  </span>
                )}
              </div>
              <div className="game-screen__points-row">
                <div
                  className={`game-screen__points${scoreFocusPlayerIndex === 0 ? ' game-screen__points--surge-tick game-screen__points--surge-big' : ''}`}
                >
                  Score: {displayUserScore}
                </div>
              </div>
              <div ref={handTargetRef} className="game-screen__hand-fan-anchor" aria-hidden />
              {tooltip && tooltipAnchor === 'fan' && (
                <div className="game-screen__tooltip game-screen__tooltip--fan" onClick={() => setTooltip(null)}>
                  <span className="game-screen__tooltip-text">{tooltip}</span>
                </div>
              )}
              <HandFan
                cards={userHandDealPlaceholders !== null ? userHandDealPlaceholders : userHand}
                selectedCardId={null}
                multiSelectedIds={handSelectedIds}
                hiddenCardIds={hiddenHandCardIds}
                onSelect={handleCardSelect}
                onReorder={handleReorder}
                onDragDiscard={
                  handSelectedIds.length <= 1 &&
                  !flyingCard &&
                  ((isUserTurn && state.phase === PHASES.PLAYING) ||
                    (state.phase === PHASES.SURGE_GRACE && userTotalCards === 11))
                    ? handleDragDiscard
                    : undefined
                }
                onDragDiscardBlocked={
                  handSelectedIds.length <= 1 &&
                  !flyingCard &&
                  ((isUserTurn && state.phase === PHASES.PLAYING) ||
                    (state.phase === PHASES.SURGE_GRACE && userTotalCards === 11))
                    ? () => showTooltip(CANNOT_DISCARD_PICKED_UP_MSG, 2600, 'fan')
                    : undefined
                }
                isDiscardDropTarget={isDiscardDropTarget}
                getPileDropIndex={pileDropInteractionEnabled ? getPileDropIndex : undefined}
                onDropOnPile={pileDropInteractionEnabled ? handleDropOnPile : undefined}
                onDragOverPileChange={pileDropInteractionEnabled ? setDragOverUserPileIndex : undefined}
                discardDisabledId={hasPickedUp ? state.lastPickedUpCardId : undefined}
                interactive={!surgeReveal && !dealDealtCounts}
                faceDown={Boolean(dealDealtCounts)}
              />
                <div className="game-screen__fan-actions">
                  <div className="game-screen__fan-actions-row">
                    <div className="game-screen__fan-primary-slot">
                      {handSelectedIds.length === 1 && (
                        <button
                          type="button"
                          className="game-screen__btn game-screen__btn--primary game-screen__btn--glow"
                          onClick={handleConfirmDiscard}
                        disabled={
                          surgeReveal ||
                          dealDealtCounts ||
                          Boolean(flyingCard) ||
                          !(
                            (state.phase === PHASES.PLAYING && isUserTurn) ||
                            (state.phase === PHASES.SURGE_GRACE && userTotalCards === 11)
                          )
                        }
                        >
                          Put card down
                        </button>
                      )}
                      {handSelectedIds.length >= 2 && (
                        <button
                          type="button"
                          className="game-screen__btn game-screen__btn--primary game-screen__btn--glow"
                          onClick={handleConfirmPile}
                          disabled={surgeReveal || dealDealtCounts}
                        >
                          Make pile ({handSelectedIds.length})
                        </button>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="game-screen__btn game-screen__btn--surg"
                    onClick={() => {
                      if (state.phase === PHASES.SURGE_GRACE) {
                        handleSurgeReady();
                      } else {
                        handleSerg();
                      }
                    }}
                    disabled={
                      (state.phase !== PHASES.PLAYING && state.phase !== PHASES.SURGE_GRACE) ||
                      surgeReveal ||
                      dealDealtCounts ||
                      (state.phase === PHASES.SURGE_GRACE &&
                        (userTotalCards !== 10 || Boolean(readyPlayers[0])))
                    }
                    aria-label="SURGE, end round"
                  >
                    <span className="label">{state.phase === PHASES.SURGE_GRACE ? 'READY' : 'SURGE'}</span>
                    <span className="gradient" />
                    <span className="transition" />
                  </button>
                </div>
                  </div>
                </div>
                {showSurgePenalty && surgePendingDetail && (
                  <div className="game-screen__surge-penalty" aria-live="polite">
                    <span className="game-screen__surge-penalty-label">Leftover cards</span>
                    <span className="game-screen__surge-penalty-val">−{surgePendingDetail.penalty}</span>
                  </div>
                )}
                {showSurgeBonus && surgePendingDetail && (
                  <div className="game-screen__surge-bonus" aria-live="polite">
                    <span className="game-screen__surge-bonus-label">All cards used bonus</span>
                    <span className="game-screen__surge-bonus-val">+{surgePendingDetail.roundShellBonus}</span>
                  </div>
                )}
                {showSurgeMaxPotential && surgePendingDetail && (
                  <div className="game-screen__surge-max-potential" aria-live="polite">
                    <span className="game-screen__surge-max-potential-label">Max possible score</span>
                    <span className="game-screen__surge-max-potential-val">
                      {surgePendingDetail.maxPotential > 0 ? '+' : ''}
                      {surgePendingDetail.maxPotential}
                    </span>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        </div>
      </div>

      {showChatPanel && chatOpen && (
        <div
          ref={chatPanelRef}
          className={`game-screen__chat-panel ${chatDragging ? 'game-screen__chat-panel--dragging' : ''} ${chatResizing ? 'game-screen__chat-panel--resizing' : ''}`}
          style={chatPanelStyle}
          role="region"
          aria-label="Game chat"
        >
          {isDesktopReveal && (
            <>
              <div
                className="game-screen__chat-resize-top"
                onPointerDown={handleChatResizePointerDown('top')}
                aria-hidden
              />
              <div
                className="game-screen__chat-resize-corner"
                onPointerDown={handleChatResizePointerDown('corner')}
                aria-hidden
              />
            </>
          )}
          <div
            className="game-screen__chat-header"
            onPointerDown={handleChatHeaderPointerDown}
          >
            <span className="game-screen__chat-header-title">Chat</span>
            <button
              type="button"
              className="game-screen__chat-close"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => setChatOpen(false)}
              aria-label="Close chat"
            >
              ×
            </button>
          </div>
          <div ref={chatListRef} className="game-screen__chat-messages">
            {chatMessages.length === 0 ? (
              <div className="game-screen__chat-empty">No messages yet.</div>
            ) : (
              chatMessages.map((msg, idx) => (
                <div
                  key={msg.id ?? `${msg.playerIndex ?? 'p'}-${msg.timestamp ?? 't'}-${idx}`}
                  className="game-screen__chat-message"
                >
                  <div className="game-screen__chat-message-head">
                    <span className="game-screen__chat-author">{msg.playerName || 'Player'}</span>
                    <span className="game-screen__chat-time">
                      {msg.timestamp
                        ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : ''}
                    </span>
                  </div>
                  <div className="game-screen__chat-text">{msg.text}</div>
                </div>
              ))
            )}
          </div>
          <div className="game-screen__chat-input-row">
            <input
              type="text"
              className="game-screen__chat-input"
              maxLength={160}
              value={chatDraft}
              placeholder="Type a message..."
              onChange={(e) => setChatDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSendChat();
                }
              }}
            />
            <button
              type="button"
              className="game-screen__chat-send"
              onClick={handleSendChat}
              disabled={!chatDraft.trim()}
            >
              Send
            </button>
          </div>
        </div>
      )}

      {dealAnimation?.flight && (
        <FlyingCard
          key={dealAnimation.flight.id}
          card={dealAnimation.flight.card}
          startPos={dealAnimation.flight.startPos}
          targetPos={dealAnimation.flight.targetPos}
          faceUp={false}
          variant="deal"
          onComplete={handleDealFlightComplete}
        />
      )}
      {flyingCard && !dealAnimation && (
        <FlyingCard
          card={flyingCard.card}
          startPos={flyingCard.startPos ?? null}
          targetPos={flyingCard.targetPos ?? null}
          onComplete={handleFlyingCardComplete}
        />
      )}
      {surgePhaseNow?.type === 'roundSplash' && (
        <div className="game-screen__round-splash" aria-hidden>
          <span>Round {surgePhaseNow.nextRound}</span>
        </div>
      )}
      {surgeCallSplash && (
        <div className="game-screen__surge-call-splash" aria-hidden>
          <span className="game-screen__surge-call-splash-line game-screen__surge-call-splash-line--title">SURGE!</span>
          <span className="game-screen__surge-call-splash-line game-screen__surge-call-splash-line--name">
            {surgeCallSplash.callerName} called it
          </span>
        </div>
      )}
      {showGameOver && gameWinnerRevealStep > 0 && (
        <div className="game-screen__winner-splash" aria-hidden>
          <span className="game-screen__winner-splash-line game-screen__winner-splash-line--title">
            Winner
          </span>
          {gameWinnerRevealStep >= 2 && (
            <span className="game-screen__winner-splash-line game-screen__winner-splash-line--name">
              {state.playerNames[state.gameWinner]}
            </span>
          )}
        </div>
      )}

      {showGameOver && (
        <div className="game-screen__modal-overlay" role="dialog" aria-modal="true">
          <div className="game-screen__modal">
            <h3>Game over</h3>
            <p>
              {state.playerNames[state.gameWinner]} wins with {state.gameScores[state.gameWinner]} points!
            </p>
            <button
              type="button"
              className="game-screen__btn game-screen__btn--primary"
              onClick={() => {
                if (mpRef.current && gameMode === 'online') {
                  mpRef.current.playAgain();
                } else {
                  resetToLobby(state);
                }
              }}
            >
              Play again
            </button>
          </div>
        </div>
      )}

      {showQuickStartOverlay && (
        <div
          className="game-screen__modal-overlay game-screen__modal-overlay--quickstart"
          role="dialog"
          aria-modal="true"
          aria-labelledby="quick-start-title"
        >
          <div className="game-screen__modal" onClick={(e) => e.stopPropagation()}>
            <div className="game-screen__modal-head">
              <h3 id="quick-start-title">How to Play</h3>
            </div>
            <div className="game-screen__modal-body">
              <div className="game-screen__quick-start-modal-list" role="list">
                {QUICK_START_STEPS.map((step) => (
                  <div key={step.keyword} className="game-screen__quick-start-modal-item" role="listitem">
                    <div className="game-screen__quick-start-modal-title">{step.keyword}</div>
                    <p className="game-screen__quick-start-modal-body">{step.body}</p>
                  </div>
                ))}
              </div>
              <div className="game-screen__modal-actions">
                <button
                  type="button"
                  className="game-screen__btn game-screen__btn--primary"
                  onClick={handleCloseQuickStart}
                >
                  Understood
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRulesOverlay && (
        <div
          className="game-screen__modal-overlay game-screen__modal-overlay--rules"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rules-title"
          onClick={() => setShowRulesOverlay(false)}
        >
          <div className="game-screen__modal" onClick={(e) => e.stopPropagation()}>
            <div className="game-screen__modal-head">
              <h3 id="rules-title">Rules</h3>
              <button
                type="button"
                className="game-screen__modal-close"
                onClick={() => setShowRulesOverlay(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="game-screen__modal-body">{renderRulesContent()}</div>
          </div>
        </div>
      )}

      {showPointsOverlay && (
        <div
          className="game-screen__modal-overlay game-screen__modal-overlay--points"
          role="dialog"
          aria-modal="true"
          aria-labelledby="points-title"
          onClick={() => setShowPointsOverlay(false)}
        >
          <div className="game-screen__modal" onClick={(e) => e.stopPropagation()}>
            <div className="game-screen__modal-head">
              <h3 id="points-title">Points & hands</h3>
              <button
                type="button"
                className="game-screen__modal-close"
                onClick={() => setShowPointsOverlay(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="game-screen__modal-body">
              <p>Your hand is scored by counting the best combination of groups.</p>
              <ul className="game-screen__points-list">
                <li><span className="game-screen__points-list-text"><strong>Pair</strong> (2 of a kind): 1 pt</span><PointsCardFan cards={POINTS_HAND_EXAMPLES[0]} /></li>
                <li><span className="game-screen__points-list-text"><strong>Triple</strong> (3 of a kind): 2 pts</span><PointsCardFan cards={POINTS_HAND_EXAMPLES[1]} /></li>
                <li><span className="game-screen__points-list-text"><strong>Four of a kind</strong>: 5 pts (four Aces: 7 pts)</span><PointsCardFan cards={POINTS_HAND_EXAMPLES[2]} /></li>
                <li><span className="game-screen__points-list-text"><strong>Full house</strong> (triple + pair): 4 pts</span><PointsCardFan cards={POINTS_HAND_EXAMPLES[3]} /></li>
                <li><span className="game-screen__points-list-text"><strong>Straight</strong> (5+ cards in sequence): 3 pts (10 cards: 8 pts)</span><PointsCardFan cards={POINTS_HAND_EXAMPLES[4]} /></li>
                <li><span className="game-screen__points-list-text"><strong>Flush</strong> (5+ same suit, not in sequence): 3 pts (10 cards: 8 pts)</span><PointsCardFan cards={POINTS_HAND_EXAMPLES[5]} /></li>
                <li><span className="game-screen__points-list-text"><strong>Straight flush</strong> (same suit in sequence): 3 pts + 1 per extra card (e.g. 5 cards = 5 pts). Royal straight flush (10–A): 10 pts.</span><PointsCardFan cards={POINTS_HAND_EXAMPLES[6]} /></li>
              </ul>
              <p>Unused cards cost 1 point each. If you use every card in your hand in scoring groups, you get a +1 bonus.</p>
              <p><strong>Winning the game:</strong> A 10-card straight flush in one suit wins the whole game. Otherwise first to 40 points wins.</p>
            </div>
          </div>
        </div>
      )}

      {showLobbyConfirm && (
        <div
          className="game-screen__modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={handleCancelLobby}
        >
          <div className="game-screen__modal" onClick={(e) => e.stopPropagation()}>
            <h3>Return to lobby?</h3>
            <p>Change players? Current game will end.</p>
            <div className="game-screen__modal-actions">
              <button type="button" className="game-screen__btn game-screen__btn--secondary" onClick={handleCancelLobby}>
                Cancel
              </button>
              <button type="button" className="game-screen__btn game-screen__btn--primary" onClick={handleConfirmLobby}>
                Go to lobby
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
